/**
 * English translations for the Calibrate Technology Project Discovery app.
 * 
 * Structure mirrors the backend translations but adapted for React usage.
 * Keys are organized by feature/page area.
 */

const en = {
    // Common / General UI elements
    common: {
        save: 'Save',
        saving: 'Saving...',
        cancel: 'Cancel',
        delete: 'Delete',
        edit: 'Edit',
        create: 'Create',
        update: 'Update',
        close: 'Close',
        confirm: 'Confirm',
        back: 'Back',
        next: 'Next',
        previous: 'Previous',
        loading: 'Loading...',
        success: 'Success',
        error: 'Error',
        warning: 'Warning',
        yes: 'Yes',
        no: 'No',
        ok: 'OK',
        search: 'Search',
        filter: 'Filter',
        sort: 'Sort',
        actions: 'Actions',
        status: 'Status',
        date: 'Date',
        name: 'Name',
        email: 'Email',
        description: 'Description',
        submit: 'Submit',
        reset: 'Reset',
        clear: 'Clear',
        all: 'All',
        none: 'None',
        select: 'Select',
        selected: 'Selected',
        required: 'Required',
        optional: 'Optional',
        view: 'View',
        download: 'Download',
        upload: 'Upload',
        remove: 'Remove',
        add: 'Add',
        more: 'More',
        less: 'Less',
        show: 'Show',
        hide: 'Hide',
        expand: 'Expand',
        collapse: 'Collapse',
        refresh: 'Refresh',
        retry: 'Retry',
        or: 'or',
        complete: 'Complete',
        incomplete: 'Incomplete',
        pending: 'Pending',
        approved: 'Approved',
        rejected: 'Rejected',
        approve: 'Approve',
        reject: 'Reject',
        noneParentheses: '(none)',
        by: 'by',
        draft: 'Draft',
        active: 'Active',
        inactive: 'Inactive',
        areYouSure: 'Are you sure?',
        noDataFound: 'No data found',
        startBuilding: 'Start Building',
        start: 'Start',
        continue: 'Continue',
        about: 'About',
        done: 'Done',
        leave: 'Leave',
        unsavedChanges: 'Unsaved Changes',
        unsavedChangesMessage: 'You have unsaved changes. Are you sure you want to leave this page?',
        sortByTitle: 'Sort by title',
        saveAllChanges: 'Save All Changes',
        change: 'Change',
        person: 'Person',
        people: 'People',
        deleting: 'Deleting...',
        confirmDelete: 'Confirm Delete',
        accept: 'Accept',
        use: 'Use',
        used: 'Used',
        saveChanges: 'Save Changes',
        summary: 'Summary',
        backToProfile: 'Back to {{label}}',
        pleaseCompleteAllRequiredFields: 'Please fill in all required fields',
        go: 'Go',
        new: 'new',
        proceed: 'Proceed',
    },

    // Assistant / AI Chat
    assistant: {
        title: 'Calibrate Assistant',
        termsTitle: 'Calibrate Assistant Terms',
        termsText1: 'The Calibrate assistant uses AI to generate responses for guidance and learning purposes only.',
        termsText2: 'AI outputs may contain errors or omissions and should not be treated as formal advice.',
        termsText3: 'Always apply your own professional judgement and follow your organisation\'s policies before taking action.',
        termsAgree: 'I have read and agree to the Terms of Use for the Calibrate AI Assistant.',
    },

    // Authentication
    auth: {
        login: 'Login',
        logout: 'Log Out',
        register: 'Register',
        forgotPassword: 'Forgot Password?',
        resetPassword: 'Reset Password',
        changePassword: 'Change Password',
        password: 'Password',
        confirmPassword: 'Confirm Password',
        rememberMe: 'Remember Me',
        loginFailed: 'Invalid email or password.',
        sessionExpired: 'Your session has expired. Please log in again.',
        unauthorized: 'You are not authorized to access this resource.',
        // Sign In / Sign Up page
        signIn: 'Sign In',
        signUp: 'Sign Up',
        createAccount: 'Create account',
        alreadyHaveAccount: 'Already have an account? Login',
        // Placeholders
        emailPlaceholder: 'Email address',
        passwordPlaceholder: 'Password',
        fullNamePlaceholder: 'Full name',
        confirmPasswordPlaceholder: 'Confirm Password',
        codePlaceholder: 'Code',
        verificationCodePlaceholder: 'Verification Code',
        currentPasswordPlaceholder: 'Enter your current password',
        newPasswordPlaceholder: 'Enter your new password',
        confirmNewPasswordPlaceholder: 'Confirm your new password',
        // Password pages
        forgotPasswordTitle: 'Forgot Password',
        forgotPasswordInstructions: 'Enter your email address and check your inbox and spam folder for a password reset code that will enable you choose a new password.',
        emailResetCode: 'Email Password Reset Code',
        rememberedPassword: 'Remembered your password? Login',
        resetPasswordTitle: 'Reset Password',
        didntReceiveCode: "Didn't receive code? Resend code",
        // Email verification
        verifyEmailTitle: 'Verify Email',
        verifyEmail: 'Verify Email',
        resendVerificationCode: 'Resend Verification Code',
        alreadyVerified: 'Already verified your account? Login',
        // Change password
        currentPassword: 'Current Password',
        newPassword: 'New Password',
        confirmNewPassword: 'Confirm New Password',
        changingPassword: 'Changing Password...',
        passwordChangedSuccess: 'Password changed successfully!',
        passwordChangeFailed: 'Failed to change password',
        // Validation messages
        validation: {
            currentPasswordRequired: 'Current password is required',
            newPasswordRequired: 'New password is required',
            confirmNewPassword: 'Please confirm your new password',
            passwordsMustMatch: 'Passwords do not match',
            passwordMinLength: 'Password must be at least 8 characters long',
            passwordUppercase: 'Password must contain at least one uppercase letter',
            passwordLowercase: 'Password must contain at least one lowercase letter',
            passwordNumber: 'Password must contain at least one number',
            passwordSpecial: 'Password must contain at least one special character',
        },
        // Password requirements checklist
        passwordRequirements: {
            title: 'Password must include:',
            minLength: 'At least {{count}} characters',
            uppercase: 'One uppercase letter (A-Z)',
            lowercase: 'One lowercase letter (a-z)',
            number: 'One number (0-9)',
            special: 'One special character (!@#$%^&*)',
            passwordsMatch: 'Passwords match',
        },
        thankYouForSigningUp: 'Thank you for signing up. We will be in touch.',
        // Create Password (invite flow)
        createPassword: {
            title: 'Create Your Password',
            instructions: 'Welcome! Please create a password to complete your account setup.',
            submit: 'Create Password',
            success: 'Password created successfully. Welcome!',
            tokenExpired: 'Your invitation link has expired. Please request a new one.',
            tokenInvalid: 'This invitation link is invalid. Please request a new one.',
            requestNewInvite: 'Request a new invitation',
            alreadyCreated: 'Already created your password? Login',
        },
        // Resend Invite
        resendInvite: {
            title: 'Resend Invitation',
            instructions: 'Enter the email address you were invited with and we will send you a new invitation link.',
            submit: 'Resend Invitation',
            success: 'If this email has a pending invitation, a new link has been sent. Please check your inbox or spam folder.',
            backToLogin: 'Back to Login',
        },
        // Invite detection
        receivedInvitation: 'Received an invitation?',
        alreadyInvited: 'You have been invited to the platform. Please check your email inbox or spam folder for your invitation link, or request a new one below.',
        // Signup page
        signup: {
            intro: 'We would love to stay in touch and show you some of the benefits Calibrate Learning and Calibrate Technology have brought to existing customers.',
            enterDetails: 'Please enter your details below and we will be in touch.',
            namePlaceholder: 'Name',
            companyPlaceholder: 'Company',
            nameRequired: 'Name is required',
            emailRequired: 'Email is required',
            invalidEmail: 'Please enter a valid email address',
            companyRequired: 'Company is required',
        },
    },

    // App-level
    app: {
        name: 'Calibrate Technology',
    },

    // Profile / Settings
    profile: {
        title: 'Profile',
        settings: 'Settings',
        language: 'Language',
        languagePreference: 'Language Preference',
        languageUpdated: 'Language preference updated successfully.',
        useOrganisationDefault: 'Use organisation default ({{language}})',
        personalPreference: 'Personal preference',
    },

    // Organisation / Tenant
    organisation: {
        title: 'Organisation',
        select: 'Select Organisation',
        create: 'Create Organisation',
        edit: 'Edit Organisation',
        delete: 'Delete Organisation',
        name: 'Organisation Name',
        defaultLanguage: 'Default Language',
        defaultLanguageHelp: 'The default language for all users in this organisation.',
        fivePlusFiveEnabled: '5+5 Feature',
        fivePlusFiveHelp: 'When enabled, staff will see task and behaviour examples for each CRE during their self-assessment submission.',
        respectExpectationsEnabled: 'Respect Expectations',
        respectExpectationsEnabledHelp: 'When enabled, staff will see and complete Respect Expectations next to Task Expectations.',
        created: 'Organisation created successfully.',
        updated: 'Organisation updated successfully.',
        deleted: 'Organisation deleted successfully.',
        noOrganisations: 'You are not associated with any organisations.',
        switch: 'Switch Organisation',
        current: 'Current Organisation',
        hello: 'Hello, {{name}}',
        noAccountAssociated: 'Thank you for registering onto the Calibrate platform. You have logged in, but it looks like your account has not yet been associated with an organisation.',
        cannotProceed: "You cannot proceed any further at this point in time, but once your account has been assigned to an organisation you will see it here and be able to proceed.",
        manageOrganisations: 'Manage Organisations',
        loading: 'Loading organisation...',
        tier: 'Tier',
        tier_essentials: 'Essentials',
        tier_standard: 'Standard',
        tier_professional: 'Professional',
        tier_enterprise: 'Enterprise',
        demo: 'Demo',
        demoEnvironment: 'Demo environment',
        demoEnvironmentHelp: 'Calibrate staff can fill a demo environment with demo data from Setup.',
    },

    // Reseller Management
    reseller: {
        manageResellers: 'Manage Partners',
        currentResellers: 'Current Partners',
        addReseller: 'Add Partner',
        remove: 'Remove',
        makeReseller: 'Make Partner',
        isReseller: 'Partner',
        noResellers: 'No Partners have been added yet.',
        noResults: 'No users found.',
        noResellersFound: 'No Partners found.',
        searchPlaceholder: 'Search users by name or email...',
        searchResellersPlaceholder: 'Search partners...',
        toggleSuccess: 'Partner status updated.',
        reassign: 'Reassign Organisation',
        reassignDescription: 'Assign "{{name}}" to a Partner.',
        reassignSuccess: 'Organisation reassigned successfully.',
        currentOwner: 'Currently assigned to: {{name}}',
        unassign: 'Unassign',
        assignTo: 'Assign',
        createdBy: 'Partner: {{name}}',
        filterByOwner: 'Filter by owner',
        filterAllOrgs: 'All organisations',
        filterCalibrate: 'Calibrate',
        org: 'org',
        orgs: 'orgs',
    },
    dashboard: {
        title: 'Dashboard',
        welcome: 'Welcome, {{name}}!',
        overview: 'Overview',
        recentActivity: 'Recent Activity',
        yourProgress: 'Your Progress',
        teamProgress: 'Team Progress',
        myPeople: 'My People',
        myPeopleDesc: 'See the people you are responsible for, their information and self-assessments',
        myValidations: 'My Validations',
        myValidationsDesc: 'See any validations that you need to complete for your people',
        outstandingValidations: '{{count}} outstanding validations to complete',
        welcomeText: 'Welcome to Calibrate. This is the portal to your own self-development and ongoing learning. Here you can access your people, your {{creAbbr}}, your {{indicatorLabel}} and your {{actionPlanLabel}}.',
        welcomeTextNoVerification: 'Welcome to Calibrate. This is the portal to your own self-development and ongoing learning. Here you can access your people, your {{creAbbr}}, your {{indicatorLabel}} and your {{actionPlanLabel}}.',
        actionPlan: 'Action Plan',
        suggestionsReady: '{{actionPlanLabel}} Suggestions Ready',
        suggestionsDescription: 'Review AI-generated suggestions and create your {{actionPlanLabel}}',
        suggestionsStats: '{{total}} suggestions available{{selected}}',
        suggestionsSelected: ', {{count}} selected',
        myCreTitle: 'My {{creAbbr}}',
        myCreDesc: 'See your current {{creAbbr}}',
        myIndicatorsTitle: 'My {{indicatorLabel}}',
        myIndicatorsDesc: 'Revisit your existing {{indicatorLabel}} or take a new self-assessment',
        myActionPlanTitle: 'My {{actionPlanLabel}}',
        myActionPlanDesc: 'See your current {{actionPlanLabel}}',
        competencyTestsDesc: 'View and complete your competency exercise',
        nextStep: {
            title: 'Your Next Step',
            reviewCres: 'Review Your {{creAbbrPlural}}',
            reviewCresDesc: 'Familiarise yourself with your {{creAbbrPlural}} before starting your self-assessment.',
            completeIndicator: 'Complete Your {{indicatorLabel}}',
            completeIndicatorDesc: '{{indicatorLabel}} in progress. Restart where you left off.',
            awaitingVerification: 'Awaiting Verification',
            awaitingVerificationDesc: 'Your {{indicatorLabel}} has been submitted and is awaiting manager verification. You\'ll be notified when it\'s reviewed.',
            reviewSuggestions: 'Review {{actionPlanLabel}} Suggestions',
            reviewSuggestionsDesc: 'You have {{count}} AI-generated suggestions ready to review for your {{actionPlanLabel}}.',
            createActionPlan: 'Create Your {{actionPlanLabel}}',
            createActionPlanDesc: 'Suggestions have been generated. Head to your {{actionPlanLabel}} to review and finalise.',
            takeCompetencyTest: 'Take Competency Exercise',
            takeCompetencyTestDesc: 'You have a competency exercise available. Complete it to demonstrate your knowledge.',
            continueCompetencyTest: 'Continue Competency Exercise',
            continueCompetencyTestDesc: 'You\'ve answered {{answered}} of {{total}} questions. Pick up where you left off.',
            allCaughtUp: 'Everything is now up to date',
            nextScheduledFor: 'Your next {{indicatorLabel}} is scheduled for {{date}}.',
            noUpcoming: 'No upcoming {{indicatorLabel}} right now. Great work!',
            stepIndicator: '{{indicatorLabel}}',
            stepVerification: 'Verification',
            stepSuggestions: 'Suggestions',
            stepActionPlan: '{{actionPlanLabel}}',
            stepCompetencyTest: 'Competency',
            stepSimulationExercise: 'Simulation',
            stepGenerating: 'Generating…',
            stepReviewCres: '{{creAbbrPlural}}',
            generatingSuggestions: 'Generating {{actionPlanLabel}} Suggestions',
            generatingSuggestionsDesc: 'Your {{actionPlanLabel}} suggestions are being generated. This usually takes about a minute.',
            generatingActionPlan: 'Generating Your {{actionPlanLabel}}',
            generatingActionPlanDesc: 'Your {{actionPlanLabel}} is being generated. This usually takes about a minute.',
            takeSimulationExercise: 'Take Simulation Exercise',
            simulationExerciseDesc: 'You have a simulation exercise available. Complete it to practice and develop your skills.',
        },
        indicatorInProgressStats: 'In progress',
        indicatorNextScheduled: 'Next: {{date}}',
        indicatorCompletedStats: '{{count}} completed',
        actionPlanStatusNone: 'Not started',
        actionPlanStatusGenerating: 'Generating…',
        actionPlanStatusSuggestions: 'Review suggestions',
        actionPlanStatusCreated: 'Created',
        validationsOverdue: '{{count}} overdue',
        simulationExerciseDesc: 'Practice and develop your skills with simulation exercises.',
    },

    simulationExercise: {
        title: 'Simulation Exercise',
        description: 'This feature will provide simulation exercises, that are relevant to current operational practices.\nYou will be expected to give your immediate response actions to the examples shown to you.',
        placeholder: 'Formal Examples will be provided when your participation is required.',
    },

    // People / Staff
    people: {
        title: 'People',
        allPeople: 'All People',
        addPerson: 'Add Person',
        editPerson: 'Edit Person',
        removePerson: 'Remove Person',
        staff: 'Staff',
        manager: 'Manager',
        admin: 'Admin',
        role: 'Role',
        businessRole: 'Business Role',
        inviteSent: 'Invitation sent successfully.',
        personAdded: 'Person added successfully.',
        personUpdated: 'Person updated successfully.',
        personRemoved: 'Person removed successfully.',
        directReports: 'Direct Reports',
        allReports: 'All Reports',
        allReportsToMe: 'All Reports to Me',
        description: 'All active people in this organisation',
        userProfile: 'User Profile',
        // People page specific
        accessRequired: 'People access required',
        noPermission: "You do not have permission to view this page.",
        removeFilter: 'remove the filter',
        viewingReportsOf: "You are viewing the reports of {{name}}. To view all people you have access to,",
        totalPeople: 'Total People',
        totalReports: 'Total people that I manage',
        totalReportsDesc: 'Total people that I manage indirectly',
        directReportsDesc: 'People that I manage directly',
        averageIndicatorScore: 'Average Self-assessment Score',
        averageIndicatorScoreDesc: 'Overall self-assessment score across everyone',
        averageIndicatorScoreDirectDesc: 'An average of all self-scores of the people that you manage',
        averageIndicatorScoreAllDesc: 'Overall self-assessment score within all people',
        employeesNeedSupport: 'Employees in need of support',
        employeesNeedSupportDesc: 'People that may require extra support',
        employeesNeedSupportDirectDesc: 'Pay attention and provide extra support to these people',
        employeesNeedSupportAllDesc: 'Pay attention and provide extra support to these people',
        errorLoadingPeople: 'Error loading people',
        errorLoadingDirectReports: 'Error loading direct reports',
        errorLoadingAllReports: 'Error loading all reports',
        errorLoadingUser: 'Error loading user information',
        // People report/profile
        my: 'My',
        allReportsTo: 'All Reports to {{name}}',
        directReport: 'Direct Report',
        totalReport: 'Total Report',
        noPeopleFound: 'No people found',
        changeRole: 'Change Role',
        selectNewRole: 'Select new role',
        roleUpdated: 'Role updated',
        errorUpdatingRole: 'Error updating role',
        startedRole: 'Started Role',
        timeInRole: 'Time in Role',
        reports: 'People managed by this person',
        seeProfile: "See {{name}}'s Profile",
        hasNoManager: '{{name}} has no manager',
        noPermissionToView: 'You do not have permission to view this.',
        // Table headers
        managerName: 'Manager Name',
        managerRole: 'Manager Role',
        indicatorScore: 'Self-assessment Score',
        noActionPlanYet: '(no action plan yet)',
        // Indicators section
        noPermissionToViewIndicators: 'You do not have permission to view {{indicatorLabelPlural}}',
        outstandingIndicator: 'Outstanding {{indicatorLabelPlural}}',
        wasDueOn: 'Was due on {{date}}',
        indicatorInProgress: '{{indicatorLabel}} In Progress:',
        startedOn: 'Started on {{date}}',
        viewIndicatorInProgress: 'View {{indicatorLabel}} In Progress',
        nextIndicator: 'Next {{indicatorLabel}}',
        willBeRequestedOn: 'Will be automatically requested on {{date}}, but you can manually request one too.',
        manualFrequency: '{{indicatorLabel}} (Manual)',
        manualFrequencyDescription: 'No scheduled due date - {{indicatorLabel}} can be requested or started at any time.',
        requestIndicator: 'Request {{indicatorLabel}}',
        addCommentToIndicator: "Add a comment to {{name}}'s next {{indicatorLabel}}",
        previousIndicators: 'Previous {{indicatorLabelPlural}}',
        noPreviousIndicatorsYet: 'No previous {{indicatorLabelPlural}} yet',
        previousRoles: 'Previous Roles',
        roleNoLongerExists: 'Role no longer exists',
    },

    // Staff management
    staff: {
        title: 'Staff',
        addStaff: 'Add Staff',
        editStaff: 'Edit Staff',
        details: 'Details',
        validationStatus: 'Validation Status',
        fixErrors: 'Please fix the highlighted errors.',
        additionalRoles: 'Additional Roles',
        permissions: 'Permissions',
        permissionsNote: 'Any changes made to permissions will require the user to log out and log back in.',
        canManageAI: 'Can manage AI Settings',
        canViewAllPeople: 'Can view all People',
        canAccessReports: 'Can access Reports',
        verifier: 'Verifier',
        noVerifierAssigned: 'No verifier assigned',
        forceApprove: 'Force Approve',
        forceDecline: 'Force Decline',
        changeVerifier: 'Change verifier?',
        changeVerifierMessage: 'This will reassign who validates this {{indicatorLabel}}. The new verifier will be given manager permissions if needed.',
        // Staff Index page
        organisationChart: 'Organisation Chart',
        filtersAndSort: 'Filters & Sort',
        clearFilters: 'Clear Filters',
        filters: 'Filters',
        searchNameOrEmail: 'Search name or email',
        filterByRole: 'Filter by role',
        filterByBusinessRole: 'Filter by business role',
        allBusinessRoles: 'All business roles',
        statusAndSort: 'Status & Sort',
        showInactive: 'Show Inactive',
        onlyInactive: 'Only Inactive',
        sortByName: 'Sort by Name',
        sortByEmail: 'Sort by Email',
        sortByBusinessRole: 'Sort by Business Role',
        sortAscending: 'Sort Ascending',
        sortDescending: 'Sort Descending',
        deactivateStaff: 'Deactivate Staff',
        deactivateStaffMessage: 'Are you sure you want to deactivate this staff member?',
        deleteStaff: 'Delete Staff',
        deleteStaffMessage: 'Are you sure you want to permanently delete this staff member? All associated data (Self-assessments, action plans, comments) will be permanently deleted. This cannot be undone.',
        staffSaved: 'Staff saved',
        inviteSent: 'Invite sent',
        inviteResent: 'Invite resent',
        failedToResendInvite: 'Failed to resend invite',
        staffDeactivated: 'Staff deactivated',
        staffDeleted: 'Staff permanently deleted',
        failedToDeactivate: 'Failed to deactivate',
        failedToDelete: 'Failed to delete',
        reactivated: 'Reactivated',
        failed: 'Failed',
        deactivate: 'Deactivate',
        reactivate: 'Reactivate',
        invite: 'Invite',
        inviteAll: 'Invite All',
        inviteAllHeader: 'Invite all non-invited staff?',
        inviteAllMessage: 'This will send invitation emails to {{count}} staff member(s).',
        inviteAllQueued: 'Sending invitations to {{count}} staff member(s).',
        failedToInviteAll: 'Failed to invite all non-invited staff',
        inviteModalTitle: 'Send Invitations',
        inviteSelected: 'Send {{count}} Invitation(s)',
        selectAll: 'Select All',
        deselectAll: 'Deselect All',
        notInvitedWithCount: 'Not Invited ({{count}})',
        invitedWithCount: 'Invited ({{count}})',
        activeWithCount: 'Active ({{count}})',
        resendInvite: 'Resend Invite',
        sending: 'Sending...',
        working: 'Working...',
        noBusinessRole: 'No business role',
        lastSeen: 'Last seen',
        never: 'Never',
        // Indicator status
        indicatorStatus: '{{indicatorLabel}} Status',
        answered: 'answered',
        noActiveIndicator: 'No active {{indicatorLabel}}',
        resetIndicator: 'Reset {{indicatorLabel}}',
        resetIndicatorHeader: 'Reset {{indicatorLabel}}?',
        resetIndicatorMessage: 'This will delete the current {{indicatorLabel}} and any answers filled out so far will be lost. This cannot be undone.',
        indicatorReset: '{{indicatorLabel}} reset',
        failedToResetIndicator: 'Failed to reset {{indicatorLabel}}',
        indicatorApproved: '{{indicatorLabel}} approved',
        indicatorSentBack: '{{indicatorLabel}} sent back',
        noSubmittedIndicators: 'No submitted {{indicatorLabelPlural}}',
        submission: 'Submission',
        verificationStatus: 'Verification Status',
        approveIndicatorHeader: 'Approve {{indicatorLabel}}?',
        approveIndicatorMessage: 'This will mark the {{indicatorLabel}} as approved.',
        declineIndicatorHeader: 'Decline {{indicatorLabel}}?',
        declineIndicatorMessage: 'This will send the {{indicatorLabel}} back to the staff member.',
        decline: 'Decline',
        clearCompetencyTest: 'Clear Current Test',
        clearCompetencyTestDescription: 'Remove the active competency exercise that has not yet been submitted.',
        clearTest: 'Clear Competency Exercise',
        clearCompetencyTestHeader: 'Clear Competency Exercise?',
        clearCompetencyTestMessage: 'This will permanently remove the current active competency exercise for this staff member. Any progress will be lost. This cannot be undone.',
        competencyTestCleared: 'Competency Exercise cleared',
        failedToClearCompetencyTest: 'Failed to clear competency exercise',
    },

    // Review Task Expectations page
    reviewTaskExpectations: {
        description1: 'Here are your Critical Role Expectations for the {{roleTitle}} role in your organisation. You can drag and drop to reorder them.',
        description2: 'Consider putting the most important Task Expectations at the top, followed by Important, and finally, Recommended.',
        confirmAndContinue: 'Confirm & Continue',
    },

    // Pagination
    pagination: {
        pageOf: 'Page {{current}} of {{total}}',
        perPage: '{{count}}/page',
    },

    // Org Chart
    orgChart: {
        title: 'Org Chart',
        noRole: 'No role',
        expand: 'Expand',
        collapse: 'Collapse',
        noHierarchy: 'No hierarchy to display.',
        noManagerAssigned: 'No manager assigned',
        failedToLoad: 'Failed to load org chart',
    },

    // Expectations / MED Builder
    expectations: {
        title: 'Expectations',
        respectExpectations: 'Respect Expectations',
        taskExpectations: 'Role Expectations',
        addExpectation: 'Add Expectation',
        editExpectation: 'Edit Expectation',
        deleteExpectation: 'Delete Expectation',
        noExpectationsForRole: 'No expectations found for this role.',
        task: 'Task',
        medBuilder: {
            title: 'Critical Role Expectations Document Builder',
            intro: "The Critical Role Expectations Document (CRE) you're about to build is a crucial tool for defining the behaviour and performance standards within your company. The CRE is composed of two core sections:",
            respectTitle: '1. Respect Expectations',
            respectDescription: "Respect Expectations are a reflection of your company's values and illustrate the attitudes and behaviours all staff members should exhibit. These expectations are universal and apply to all roles within your company.",
            taskTitle: '2. Task Expectations (by role)',
            taskDescription: 'Task expectations are specific to each role in your organisation. They set a clear standard for what needs to be done for someone to be successful in that job.',
            guidedProcess: "This builder will guide you through each step of creating a comprehensive, clear and effective CRE. It's a straightforward process that will make a significant impact on your organisation's culture and success.",
            letsGetStarted: 'Start Now',
            // Explained page
            explained: '{{label}} Explained',
            startOver: 'Start Again',
            enterDetailsToStart: 'To start creating your CRE, please enter your details below and then tap submit.',
            consentLabel: 'I consent for Calibrate Technology Ltd. to contact me regarding the Calibrate Technology suite of products.',
            continueQuestion: 'Continue?',
            continueOrStartOver: 'Continue with existing CRE or start again?',
            introText: 'In preparation for your work with The Calibrate Critical Expectations Builder it is important to understand what Respect means in the context of critical expectations.',
            simpleExamples: 'Some simple examples:',
            builderDescription: 'The Calibrate CRE Builder provides you with the opportunity to define and create a strong foundation of respect and expected outcomes across your organisation, impacting the resultant behaviours of staff at all levels.',
            examples: {
                arrivingOnTime: 'Arriving at work on time',
                understandingGuidelines: 'Understanding corporate guidelines and company policies',
                supportColleagues: 'Support and appreciate your colleagues',
                justCulture: "Contribute to 'Just Culture' allowing people to be honest",
                admitMistakes: 'Admitting your mistakes',
                treatEqually: 'Treating people equally, regardless of position',
            },
        },
        defineValues: 'Define Values',
        defineRoles: 'Define Roles',
        defineRespect: 'Define Respect',

        // CRE Page
        cre: {
            respectExpectations: 'Respect Expectations',
            taskExpectations: 'Role Expectations',
            otherRoles: 'Other Roles',
            notCreatedYet: "It looks like your {{creAbbr}} hasn't been created yet, or you have not yet been assigned to a role in your business.",
        },

        // ViewTask page
        viewTask: {
            title: 'Role Expectations',
            titleWithRole: '{{roleName}}',
            description: 'These expectations are specific to your position.',
            aboutTitle: 'About Task plus Behaviour & Attitude Expectations',
            aboutDescription: 'Role expectations provide clear performance standards for each position. These guidelines are key to individual success and collective excellence. We welcome open dialogue and will be supporting ongoing learning.',
        },

        // ViewOther page
        viewOther: {
            title: 'Role Expectations for Other Roles',
            titleWithRole: 'Role Expectations for {{roleName}}',
            backToRoles: 'Back to Roles',
            description: 'Below are listed all of the roles in your Company. Select a job role to see the Expectations.',
            aboutTitle: 'About',
            aboutDescription1: 'Here you can view the expectations for other roles in the company.\n\nTask and Behaviour & Attitude Expectations are defined for each role and may differ between roles.',
            aboutDescription2: 'Select a job role to see the expectations for your selected position.',
        },

        // ExpectationContent component
        linkedWith: 'Linked with',
        professionalStandard: 'Professional Standard',

        // Task Considerations page
        taskConsiderations: {
            title: 'Add a Task Considerations Statement',
            description1: 'You are nearly there! After defining your Roles and Task Expectations, the final step in this section is to create a Task Considerations Statement. This statement will accompany the Task Expectations in the final CRE, providing additional guidance on their interpretation and importance.',
            description2: 'Below is the default Calibrate Task Considerations statement. You can edit this to suit your needs. Tap in the box to change the content.',
            placeholder: 'Enter your Task Considerations Statement here',
        },
        reviewExpectations: 'Review Expectations',
        // DefineRespect page
        defineRespectExpectations: 'Define your Respect Expectations',
        deleteExpectationHeader: 'Delete expectation?',
        confirmDeleteRespectExpectation: 'Are you sure you want to delete this respect expectation? This action cannot be undone.',
        respectDescription1: 'Respect Expectations are a reflection of your company values and illustrate the attitudes & behaviours all staff members should exhibit.',
        respectDescription2: 'These expectations are universal and apply to all roles within your company.',
        sortByValue: 'Sort by value',
        pleaseAddAtLeastOneRespect: 'Please add at least one respect expectation.',
        respectExpectationsSaved: 'Respect expectations saved successfully!',
        toggleSuggestions: 'Toggle Suggestions',
        addNewExpectation: 'Add New Expectation',
        suggestions: 'Suggestions',
        suggestionsDescription: 'Use the Calibrate AI tool to suggest some expectations based on what you have defined in your company values.',
        generating: 'Generating...',
        generateSuggestions: 'Generate suggestions',
        generateMoreSuggestions: 'Generate more suggestions',
        // DefineTaskExpectations page
        errorLoadingTaskExpectations: 'Error loading task expectations',
        confirmRemoveTaskExpectation: 'Are you sure you want to remove this task expectation?',
        taskExpectationRemoved: 'Task expectation removed',
        taskExpectationsSaved: 'Task expectations saved successfully!',
        errorSavingTaskExpectations: 'Error saving task expectations',
        invalidResponseFormat: 'Invalid response format',
        errorGeneratingSuggestedOrder: 'Error generating suggested order',
        errorGeneratingSuggestions: 'Error generating suggestions',
        errorLoadingProfessionalStandards: 'Error loading professional standards',
        professionalStandardAdded: 'Professional standard expectation added',
        editTaskExpectation: 'Edit Task Expectation',
        suggestOrder: 'Suggest Order',
        suggestingOptimalOrder: 'Suggesting the optimal order for your Task Expectations, please wait...',
        suggestedOrderResult: 'Suggested order for your task expectations:',
        noExpectationsInSuggestedOrder: 'No task expectations found in the suggested order.',
        noSuggestionsAvailable: 'No suggestions available. Please try again.',
        linkToProfessionalStandard: 'Link to Professional Standard',
        noProfessionalStandardsFound: 'No Professional Standards Found',
        createProfessionalStandardsFirst: 'Create professional standards first in the Setup section to link them to task expectations.',
        selectProfessionalStandardExpectation: "Select a professional standard expectation to add to this role's task expectations:",
        untitledStandard: 'Untitled Standard',
        expectationCount: '{{count}} expectation{{count, plural, one {} other {s}}}',
        noExpectationsDefinedForStandard: 'No expectations defined for this standard.',
        confirmDeleteRoleWithExpectations: 'Are you sure you want to remove this role? All associated Task Expectations will also be removed.',
        youHaveUnsavedChanges: 'You have unsaved changes',
        clickSaveChangesToSave: "Click \"Save Changes\" to save your changes to the role's Task Expectations.",
        editAndAddTaskExpectationsDescription: 'Edit and add the Task Expectations we have generated but make sure to also add your own Task Expectations so that your final {{medSingular}} reflects your organisation.',
        selectOrCreateBetween: 'Select or create between 10-20 Task Expectations for {{roleName}}.',
        addTaskExpectation: 'Add Task Expectation',
        noTaskExpectationsYet: 'No Task Expectations yet. Click the button above to create one, or have Calibrate generate some for you.',
        saveTaskExpectations: 'Save Task Expectations',
        aiSuggestions: 'AI Suggestions:',
        useAiToolDescription: "Use Calibrate's AI tool to generate Task Expectations for this role.",
        roleNotFound: 'Role not found',
        // 5+5 Feature - Examples
        editExamples: 'Edit Outcomes',
        editExamplesDescription: 'Add task and behaviour outcomes to help staff understand what is expected for this CRE.',
        taskExamples: 'Task Outcomes',
        behaviourExamples: 'Attitude & Behaviour Outcomes',
        viewExamples: 'View Outcomes',
        examples: 'The Task Outcomes and Behaviour & Attitude Outcomes below define this CRE expectation.',
        noTaskExamples: 'No task outcomes added yet.',
        noBehaviourExamples: 'No behaviour outcomes added yet.',
        taskExamplePlaceholder: 'Enter a task outcome...',
        behaviourExamplePlaceholder: 'Enter a behaviour outcome...',
        examplesSaved: 'Examples saved successfully!',
        errorSavingExamples: 'Error saving examples',
        // 5+5 Feature - AI Outcome Suggestions
        aiOutcomeSuggestions: 'AI Outcome Suggestions',
        outcomeSuggestionsDescription: "Use Calibrate's AI tool to generate outcome suggestions for this expectation.",
        generateOutcomeSuggestions: 'Generate suggestions',
        generateMoreOutcomeSuggestions: 'Generate more suggestions',
        suggestedTaskOutcomes: 'Suggested Task Outcomes',
        suggestedBehaviourOutcomes: 'Suggested Attitude & Behaviour Outcomes',
        useAllTaskSuggestions: 'Use all',
        useAllBehaviourSuggestions: 'Use all',
        errorGeneratingOutcomeSuggestions: 'Error generating outcome suggestions',
        noOutcomeSuggestionsAvailable: 'No suggestions available. Please try again.',
    },

    // Indicators
    indicators: {
        title: 'Self-assessments',
        indicator: 'Self-assessment',
        startIndicator: 'Start Self-assessment',
        completeIndicator: 'Complete Self-assessment',
        verify: 'Verify',
        compliant: 'Compliant',
        nonCompliant: 'Non-Compliant',
        partiallyCompliant: 'Partially Compliant',
        pending: 'Pending',
        inProgress: 'In Progress',
        completed: 'Completed',
        verified: 'Verified',
        startNow: 'Start now',
        submitIndicator: 'Submit Self-assessment',
        submitConfirmMessage: 'Are you happy with your answers? Once your self-assessment is submitted, it will be sent to your manager for validation.',
        submitConfirmMessageWithTest: 'Are you happy with your answers? Once your self-assessment is submitted, it will be sent to your manager for validation.',
        submitConfirmMessageNoVerification: 'Are you happy with your answers? Once your self-assessment is submitted, we will start generating your {{actionPlanLabel}}.',
        submitConfirmMessageNoVerificationWithTest: 'Once you click on "submit" You will have successfully submitted your self-assessment.\n\nPlease now go to the competency exercise tab, where you will shortly see a notification to conduct your personalised competency exercise.',
        submittedSuccessfully: 'Self-assessment submitted successfully. Once validated by your manager, we will start suggesting some actions for you to take. If your manager rejects any of your answers, you will have the opportunity to review them and resubmit.',
        submittedSuccessfullyWithTest: 'Self-assessment submitted successfully. Once validated by your manager, you will be given a competency test to complete. If your manager rejects any of your answers, you will have the opportunity to review them and resubmit.',
        submittedSuccessfullyNoVerification: 'Self-assessment submitted successfully. We will now start generating your {{actionPlanLabel}} with suggested actions for you to take.',
        submittedSuccessfullyNoVerificationWithTest: 'Self-assessment submitted successfully. You will now be given a competency exercise to complete. Once finished, your {{actionPlanLabel}} will be generated.',
        failedToSubmit: 'Failed to submit self-assessment',
        approvedSuccessfully: 'self-assessment approved. The employee has been notified. We will start suggesting some actions for them and notify them when they are ready.',
        approvedSuccessfullyWithTest: 'Self-assessment approved. The employee has been notified and will be given a competency test to complete before their {{actionPlanLabel}} is generated.',
        failedToApprove: 'Failed to approve Self-assessment',
        sentBackSuccessfully: 'self-assessment sent back. The employee has been notified. You will be notified when they resubmit with changes.',
        failedToSendBack: 'Failed to send back self-assessment',
        seeExamples: 'See Examples',
        // Indicator page
        yourIndicator: 'Your Self-assessment',
        indicatorDescription: 'Instructions:\n\n\nSelect the statements you can consistently demonstrate in your daily work.\n\n\nStatements not selected will identify areas where support and development may be needed.\n\n\nBe honest, This assessment is used to provide the right help and support, not to judge your performance.',
        indicatorDescriptionReview: 'Now review your answers before submitting your {{label}}. If you are happy with your answers, scroll to the bottom to submit. If you are not happy with your answers, you can go back and change them.',
        confirmAnswer: 'I confirm I am happy with my answer to this question',
        cannotSubmitUnconfirmed: 'Your {{indicatorLabel}} cannot be submitted until all questions are confirmed. Please confirm the following:',
        yourRole: 'Your role:',
        indicatorFirstCreatedOn: 'Self-assessment first created on',
        answeredOutOf: 'You have answered {{answered}} out of {{total}} questions.',
        validateAllQuestions: 'Validate all of the questions by approving or rejecting them to submit this validation.',
        submitYourValidation: 'Submit your validation',
        validationCompleteInstructions: 'Now that you have validated all of the answers, you can either send the self-assessment back to the employee for further information, or you can approve it.',
        sendBackExplanation: 'If you send back, the employee will receive an email notification asking them for more information. They will be able to edit their answers, and once resubmitted, you will re-validate before the system will generate their action plan.',
        approveExplanation: 'If you approve the self-assessment, a personalised {{actionPlanLabel}} will be generated for the employee based on the answers given.',
        approveExplanationWithTest: 'If you approve the self-assessment, the employee will be given a competency exercise to complete. Once finished, a personalised {{actionPlanLabel}} will be generated based on their answers.',
        validationSummary: 'Out of {{total}} answers, you have approved {{approved}} and rejected {{rejected}}.',
        rejectedAnswersExplanation: 'You have rejected {{count}} answers, which means the self-assessment cannot yet be approved.',
        sendBackNextSteps: 'The next step is to send back for more information, which means that the employee will receive an email asking them for more detail. They will see the feedback you provided and be given the opportunity to resubmit with the information you require in order to approve their answers. You will then need to re-validate before the system will generate their action plan.',
        sendBackMeaning: 'This might mean that they need to provide more information, or that they need to change their answer. You will be notified when they resubmit.',
        approveIndicator: 'Approve self-assessment',
        approveConfirmMessage: 'Are you sure you want to approve this self-assessment?',
        sendBackConfirmMessage: 'Are you sure you want to send this self-assessment back for more information?',
        sendBackForMoreInfo: 'Send back for more information',
        validationIncomplete: 'Validation incomplete',
        validationIncompleteMessage: 'You have not yet validated all questions. Please approve or reject all questions before submitting.',
        number: 'Number',
        submittedAt: 'Submitted at',
        managerRole: 'Manager Role',
        validationStatus: 'Validation Status',
        validatedAt: 'Validated at',
        previousIndicator: 'Previous self-assessment',
        validateIndicator: 'Validate self-assessment',
        for: 'for',
        previousIndicatorDescription: 'Here you can see the answers you gave on your previous self-assessment',
        validateIndicatorDescription: 'Here you can see the answers given by {{name}} on their self-assessment. If you leave this page, your answers will be saved so you can continue later.',
        validated: 'Validated',
        approved: 'Approved',
        rejected: 'Rejected',
        // Index page
        myIndicators: '{{label}}',
        indicatorsPageDescription: '{{label}} help you review your current performance against your {{crePlural}}.\n\nThey identify strengths and areas where support may be needed.\n\nOnly select statements you could consistently demonstrate in your daily work.\n\nPlease be honest with your {{labelLower}} so that your personal {{actionPlanLabel}} will be more valuable to you.',
        indicatorDue: '{{label}} Due',
        startItNow: 'Your next {{label}} is due. Start it now...',
        awaitingVerification: '{{label}} Awaiting Verification',
        awaitingVerificationDescription: 'You submitted an {{label}} that is awaiting verification from your manager. You can view your answers but not make any changes.',
        processingIndicator: '{{label}} Processing',
        processingIndicatorDescription: 'Your {{label}} has been submitted and is being processed. Your {{actionPlanLabel}} will be available shortly.',
        processingIndicatorDescriptionWithTest: 'Your {{label}} has been submitted. You will be given a competency exercise to complete before your {{actionPlanLabel}} is generated.',
        indicatorInProgress: '{{label}} In Progress',
        rejectedIndicator: 'Rejected {{label}}',
        rejectedDescription: 'Your {{label}} was rejected. Click the button below to view and revise your answers. You will then be able to resubmit your {{label}} for revalidation.',
        pickUpWhereYouLeftOff: 'Continue your {{label}} from where you last stopped.',
        previousIndicators: 'Previous {{label}}',
        viewPreviousDescription: 'Here you can view your previous {{label}} and the information used to build your {{actionPlanLabel}}, including the results of your competency exercises.',
        noPreviousIndicators: "You have not taken any {{label}} yet. When you have, you will be able to view previous {{label}} and the {{actionPlanLabel}} that were generated.",
        viewPrevious: 'View previous {{label}}',
        nextIndicator: 'Next {{label}}',
        nextIndicatorDueFrom: 'Your next {{label}} will be due from {{date}}.',
        startNewIndicator: 'Start New {{label}}',
        manualFrequencyDescription: 'You can start a new {{label}} whenever you are ready.',
        // Request indicator
        requestIndicator: 'Request {{label}}',
        requestIndicatorConfirm: 'Are you sure you want to request a new {{label}} from {{name}}?',
        errorLoadingUser: 'Error loading user information',
        // Management page
        managementIndicator: 'Management {{label}}',
        employeesWithResults: 'Employees with results',
        employeesWithoutResults: 'Employees without results',
        averageLevelOfCompliance: 'Average level of compliance',
        employeesOfConcern: 'Employees of concern',
        highConcern: 'High concern',
        mediumConcern: 'Medium concern',
        lowConcern: 'Low concern',
        numberOfEmployees: 'Number of employees',
        results: 'Results',
        name: 'Name',
        estLevelCompliance: 'Est. level compliance',
        potentialMentor: 'Potential Mentor',
        demo: 'Demo',
        demoNotImplemented: 'This is a demo of the action plan viewer. It is not yet implemented.',
        noPreviousFound: 'No previous {{label}} found',
        previousIndicatorsAbout: 'Here you can see all {{indicatorLabel}} that you have taken previously. They are ordered with the most recent first. You can not change any of your responses, but you can see the answers you gave and the {{actionPlanLabel}} that was created for you.',
        // Indicator form answers
        answerNotAware: 'Not yet demonstrated consistently: I do not yet perform this consistently and will require support to meet expectations',
        answerLimitedKnowledge: 'Developing capability: I can perform this in some situations but need guidance, reminders or support',
        answerWhenReminded: 'Consistently Effective: I perform this reliably to the expected standard without support',
        answerMajorFocus: 'Strong and Proactive: I consistently perform this to a high standard and actively improve or support others',
        enterExamplePlaceholder: 'Enter your example here...',
        optionalCommentsPlaceholder: 'You can optionally enter some comments here if you would like to explain or request further support',
    },

    // Roles / Define Roles page
    roles: {
        defineYourRoles: 'Define your Roles',
        roleName: 'Role Name',
        enterRoleName: 'Enter role name...',
        category: 'Category',
        enterCategory: 'Enter category...',
        indicatorFrequency: 'Self-assessment Frequency',
        selectFrequency: 'Select frequency...',
        editRole: 'Edit Role',
        addNewRole: 'Add New Role',
        createRole: 'Create Role',
        updateRole: 'Update Role',
        saveRole: 'Save Role',
        deleteRole: 'Delete Role',
        noCategory: 'No Category',
        roleCreated: 'Role created successfully',
        roleUpdated: 'Role updated successfully',
        roleDeleted: 'Role deleted successfully',
        failedToSaveRole: 'Failed to save role',
        failedToDeleteRole: 'Failed to delete role',
        errorDeletingRole: 'Error deleting role',
        pleaseEnterRoleName: 'Please enter a role name',
        pleaseSelectReassignRole: 'Please select a role to reassign users to',
        errorGeneratingSuggestions: 'Error generating suggestions',
        errorLoadingRoles: 'Error loading roles',
        roleHasPeopleWarning: 'This role has {{count}} people assigned. You must reassign them to another role before deletion.',
        roleCanBeDeleted: 'This role has no people assigned and can be safely deleted.',
        reassignPeopleTo: 'Reassign People To',
        selectARole: 'Select a role...',
        confirmDeleteRole: 'Are you sure you want to delete the role "{{name}}"?',
        willReassignPeople: 'This will reassign {{count}} people to "{{roleName}}".',
        pleaseSelectRoleBeforeConfirming: 'Please select a role to reassign users to before confirming.',
        taskExpectationsDescription: "Task Expectations are unique to each role. To begin, type the name of a role in your organisation in the field below.",
        generateExpectationsDescription: "Once you've selected a role, we'll help you generate some role Expectations.",
        indicator: 'Self-assessment',
        suggestions: 'Suggestions',
        aiSuggestionsDescription: "Use the Calibrate AI tool to suggest some roles. We'll take into consideration the roles and categories you've already created.",
        generateSuggestions: 'Generate suggestions',
        generateMoreSuggestions: 'Generate more suggestions',
        aiContext: 'AI Context',
        aiContextPlaceholder: 'Describe the purpose and focus areas of this role to help AI generate more relevant content...',
        aiContextHelp: 'This context is used by AI when generating Action Plans, rating self-assessment answers, and suggesting expectations for this role.',
        fivePlusFiveEnabled: '5+5 for this role',
        fivePlusFiveHelp: "When off, staff in this role won't see 5+5 task and behaviour examples, even though 5+5 is on for the organisation.",
        roleDescription: 'Role Description',
        roleDescriptionPlaceholder: 'A description connecting this role to your organisation\'s goals and values...',
        roleDescriptionHelp: 'This description helps employees understand how their role contributes to the organisation\'s success. It is shown when viewing CREs.',
        generateDescription: 'Generate Description',
        regenerateDescription: 'Regenerate Description',
        generatingDescription: 'Generating...',
        regenerateDescriptionWarning: 'This will replace the current description. Continue?',
        descriptionGenerated: 'Role description generated successfully',
        failedToGenerateDescription: 'Failed to generate role description',
        needCresToGenerate: 'Add CREs to this role before generating a description.',
        needStrategicObjectivesToGenerate: 'Set strategic objectives in Company Information before generating a description.',
    },

    // Action Plans
    actionPlans: {
        title: 'Action Plans',
        createActionPlan: 'Create Action Plan',
        viewActionPlan: 'View Action Plan',
        editActionPlan: 'Edit Action Plan',
        deleteActionPlan: 'Delete Action Plan',
        actions: 'Actions',
        addAction: 'Add Action',
        dueDate: 'Due Date',
        progress: 'Progress',
        suggestedActions: 'Targeted Actions',
        customActions: 'Custom Actions',
        fixedActions: 'Fixed Actions',
        fixedActionsDescription: '{{label}} are defined by your organisation and are mandatory. They have been pre-selected for you here.',
        // Page: ActionPlan.tsx
        yourRole: 'Your Role',
        manager: 'Manager',
        managerRole: 'Manager Role',
        levelOfConcern: 'Level of Concern',
        compliance: 'Compliance',
        previousActionPlans: 'Previous {{label}}',
        currentActionPlan: 'Current {{label}}',
        previousLabel: 'Previous {{label}}',
        noActionPlanYet: 'No {{label}} yet.',
        teamMember: 'Team member',
        backToProfile: "Back to {{name}}'s profile",
        aboutActionPlans: 'Here are all of the {{label}} that have been assigned to you.',
        howGenerated: 'How are {{label}} generated?',
        howGeneratedAnswer: "Using our fine-tuned AI model, we have combined decades of management and leadership experience in conjunction with the latest regulatory guidelines.",
        aiProgrammedInfo: "Our AI model has been programmed with information about your business, your role, your critical role expectations and your answers to the {{indicatorLabel}}, to give you a fair and impartial set of recommendations.",
        combinedRecommendations: "Combining the recommendations from our AI model with your own expertise and insights created the {{label}} that you will see here.",
        // Toast messages
        actionRemovedFromPlan: 'Action removed from plan',
        actionAddedToPlan: 'Action added to plan',
        failedToUpdateAction: 'Failed to update action',
        failedToLoadSuggestedActions: 'Failed to load personalized actions',
        personalisedActionAdded: 'Personalised action added successfully',
        failedToAddPersonalisedAction: 'Failed to add personalised action',
        personalisedActionUpdated: 'Personalised action updated successfully',
        failedToUpdatePersonalisedAction: 'Failed to update personalised action',
        personalisedActionRemoved: 'Personalised action removed',
        failedToRemovePersonalisedAction: 'Failed to remove personalised action',
        pleaseSelectAtLeastOneAction: 'Please select at least one action before finalizing',
        actionPlanCreatedSuccessfully: '{{label}} created successfully',
        suggestedActionUpdated: 'Targeted Actions Updated',
        // Labels
        mandatory: 'Mandatory',
        examples: 'Examples:',
        relatedTo: 'Related to:',
        because: 'Because:',
        riskIfNotAddressed: 'Risk if not addressed:',
        successCriteria: 'Success Criteria:',
        yourPersonalisedActions: 'Your Personalised Actions',
        editPersonalisedAction: 'Edit Personalised Action',
        addPersonalisedAction: 'Add a Personalised Action',
        actionsSelected: 'Actions Selected',
        fixedActionsDefinedByOrg: '{{label}} defined by your organisation',
        aiActionsSelected: 'AI actions selected',
        personalisedActionsAdded: 'personalised actions added',
        finaliseObjectives: 'Finalise Objectives',
        finaliseObjectivesDescription: 'Once you are satisfied with the actions on this page, click the button below to Finalise your Objectives.\n\nAfter finalising, your objectives will be saved until your next {{label}}.',
        selectAtLeastOneAction: 'Select at least one action to continue',
        removeCustomAction: 'Remove Custom Action',
        removeCustomActionConfirm: 'Are you sure you want to remove this custom action? This cannot be undone.',
        saveMyObjectives: 'Save my Objectives',
        saveObjectivesConfirm: "We'll save your selected actions as your {{label}}. You can view and track progress afterwards.",
        suggestedActionsDescription: "Personalized Action Points are generated by Calibrate's AI assistant, and are supported by the critical criteria, related to the 12 Most Critical SOPs and the 6 Worst Performing SOPs",
        managerViewingNotice: "These are {{possessive}} Personalized Actions. You can make changes here and save them on their behalf.",
        actionPlanAlreadyCreated: '{{label}} already created',
        alreadyFinalisedSelectionsDescription: "You've already finalised your selections for this indicator. You can view or track progress on your {{label}} anytime.",
        viewMyActionPlan: 'View my {{label}}',
        actionPlanSuggestions: '{{label}} Suggestions',
        reviewSuggestedActionsNote: 'Review the personalized development actions below, identified from your self-assessment, competency results, and simulation exercises. Use the Calibrate Chatbot for ongoing support in completing these actions.',
        // New keys for i18n conversion
        suggestions: '{{label}} Suggestions',
        possessiveSuggestions: "{{possessive}} {{label}} Suggestions",
        thisTeamMember: 'this team member',
        possessiveProfile: "{{name}}'s profile",
        alreadyCreated: '{{label}} already created',
        alreadyFinalisedSelections: "You've already finalised your selections for this self-assessment. You can view or track progress on your {{label}} anytime.",
        failedToCreateActionPlan: 'Failed to create action plan',
        // Evidence / Examples
        yourEvidence: 'Your Evidence',
        addEvidence: 'Add Evidence',
        saveEvidence: 'Save Evidence',
        evidencePlaceholder: 'Describe your evidence of progress against this action. Be specific about what you did, when, and what the outcome was.',
        questionsToConsider: 'Questions to consider',
        exampleAdded: 'Evidence added successfully',
        failedToAddExample: 'Failed to add evidence',
        exampleUpdated: 'Evidence updated successfully',
        failedToUpdateExample: 'Failed to update evidence',
        exampleDeleted: 'Evidence deleted',
        failedToDeleteExample: 'Failed to delete evidence',
        deleteExample: 'Delete Evidence',
        deleteExampleConfirm: 'Are you sure you want to delete this evidence? This cannot be undone.',
        noEvidenceYet: 'No evidence has been submitted yet.',
        noEvidenceYetOwner: 'You haven\'t added any evidence yet. Add evidence of your progress to share with your manager before your next self-assessment',
        previousEvidence: 'Your Previous Evidence',
        previousEvidenceDescription: 'Evidence you submitted against your action plan for this expectation. Use this to help inform your answers below.',
        useEvidence: 'Use',
        // ActionPlanViewer component
        createdOn: 'Created on:',
        actionPlanDescription: 'Your action plan is a personalised set of actions that you should take to improve your compliance with the {{creLabel}}. It is based on your responses to the {{indicatorLabel}}.',
        becauseYouSaid: 'Because you said:',
        whenAskedAbout: 'when asked about',
        ifThisIsNotDone: 'If this is not done:',
        viewRelatedIndicator: 'View related {{indicatorLabel}}',
        backToPreviousPlans: 'Back to Previous {{label}}',
    },

    // Company Values
    values: {
        defineYourValues: 'Define your values',
        valuesDescription: "Your values are the bedrock of your Respect Expectations. They define your ethos and provide a framework for expected behaviours. Our system uses them to help generate Task and Respect expectations, and they are used in your team member's Action Plans, ensuring that recommendations align with your Values.",
        deleteThisValue: 'Delete this value?',
        deleteValueMessage: 'If you have any Respect Expectations that use this value, the value will be removed so that you can reassign the Respect Expectation to another value.',
        pleaseAddAtLeastOneValue: 'Please add at least one company value before saving',
        savedSuccessfully: 'Company values saved successfully!',
        suggestionsDescription: "Use Calibrate's AI tool to generate some suggestions, customised to your organisation.",
        value: 'Value',
        addAnotherValue: 'Add another value',
        addAValue: 'Add a value',
    },

    // Validation
    validation: {
        title: 'Validations',
        pendingValidation: 'Pending Validation',
        validated: 'Validated',
        rejected: 'Rejected',
        approve: 'Approve',
        reject: 'Reject',
        sendBack: 'Send Back',
        allValidations: 'All Validations',
        overdueValidations: 'Overdue Validations',
        errorFetching: 'Error fetching validations.',
        noOutstandingValidations: "You're caught up! There are no outstanding validations.",
        noOverdueValidations: "You're caught up! There are no overdue validations.",
        // Table headers
        role: 'Role',
        indicatorSubmissionDate: 'Self-assessment Submission Date',
        timeSinceSubmission: 'Time Since Submission',
        overdue: 'Overdue',
    },

    // Notifications
    notifications: {
        title: 'Notifications',
        markRead: 'Mark as Read',
        markAllRead: 'Mark All as Read',
        noNotifications: 'No notifications.',
        clearAll: 'Clear All',
        errorLoading: 'Error loading notifications',
        upToDate: "You are up to date!",
    },

    // Errors
    errors: {
        generic: 'Something went wrong. Please try again.',
        notFound: 'The requested resource was not found.',
        forbidden: 'You do not have permission to perform this action.',
        validationFailed: 'Validation failed. Please check your input.',
        serverError: 'A server error occurred. Please try again later.',
        networkError: 'A network error occurred. Please check your connection.',
        pageNotFound: 'Page Not Found',
        pageNotFoundMessage: 'The page you are looking for does not exist.',
    },

    // File Upload
    upload: {
        dropFiles: 'Drop the files here...',
        dragAndDrop: 'Drag and drop files here, or click to upload from your computer',
        tapToSelect: 'Tap here to select a file',
        maxFiles: 'You can only upload up to {{count}} files.',
        fileName: 'File Name',
        progress: 'Progress',
        confirmRemove: 'Are you sure you want to remove this file?',
        supportingMaterial: 'Here you can upload up to 3 files that support the examples you provided above. It is not compulsory to upload supporting material, but encouraged.',
    },

    // Menu / Navigation
    menu: {
        home: 'Home',
        dashboard: 'Dashboard',
        people: 'People',
        reports: 'Reports',
        simulationExercise: 'Simulation Exercise',
        setup: 'Setup',
        settings: 'Settings',
        help: 'Help',
        signOut: 'Sign Out',
        myProfile: 'My Profile',
        changePassword: 'Change Password',
        notifications: 'Notifications',
        selectLanguage: 'Select Language',
    },

    // Setup pages
    setup: {
        title: 'Setup',
        company: 'Company',
        staff: 'Staff',
        roles: 'Roles',
        values: 'Values',
        professionalStandards: 'Professional Standards',
        fixedActions: 'Fixed Actions',
        aiSettings: 'AI Settings',
        companyInformation: 'Company Information',
        companyValues: 'Company Values',
        aiSettingsNoAccess: "You do not have access to manage AI Settings. Ask an admin to grant the \"AI Settings\" permission for your account.",
        translations: 'Translations',
        emailTemplates: 'Email Templates',
        actionPlanTester: 'Action Plan Tester',
        competencyQuestions: 'Competency Questions',
        seedData: 'Seed Data',
    },

    seedData: {
        title: 'Seed Data',
        description: 'Fill this demo organisation with demo roles, people and history. Seeding runs in the background and emails you when it finishes. Demo people cannot log in and are never emailed.',
        options: 'What to seed',
        contentSource: 'Content',
        sourceAi: 'Generate for this business',
        sourceAiHelp: 'AI writes the roles, expectations, 5+5 examples and exercise questions for the business you describe. This takes several minutes.',
        sourcePack: 'Built-in pack',
        sourcePackHelp: 'Ready-made content with no AI, so it is quick and always works.',
        businessDescription: 'Describe the business',
        businessDescriptionPlaceholder: 'For example: regional airport ground handling, 400 staff',
        pack: 'Pack',
        employees: 'Number of people',
        employeesHelp: 'Between {{min}} and {{max}}, across 5 roles of 10 expectations each.',
        historyMonths: 'History',
        monthsOption: '{{count}} months',
        assessmentStyle: 'Assessment style',
        styleFivePlusFive: '5+5',
        styleYesNo: 'Yes/No only',
        fivePlusFiveRoles: 'Roles using 5+5',
        fivePlusFiveRolesOption: '{{count}} of 5',
        performanceMix: 'Performance mix',
        mix_balanced: 'Balanced',
        mix_strong: 'Strong',
        mix_struggling: 'Struggling',
        managerVerification: 'Manager verification',
        clearExisting: 'Clear existing data first',
        clearExistingHelp: "Removes this organisation's people (except Calibrate staff), roles, expectations and all assessment history. Settings, company values and email templates are kept.",
        clearRequired: 'This organisation already has roles or people, so it must be cleared first.',
        confirmName: 'Type {{name}} to confirm',
        needName: 'To start, type the organisation name exactly as shown: {{name}}',
        needDescription: 'To start, describe the business in at least 3 characters.',
        needPack: 'To start, choose a pack.',
        settingsNote: 'Seeding also sets this organisation to Yes/No questions, monthly assessments, and competency exercises followed by action plans with manager review.',
        start: 'Seed data',
        started: 'Seeding has started. You will get an email when it finishes.',
        error: 'Seeding could not start.',
        notDemo: 'This organisation is not a demo environment. Mark it as one when editing it on the organisation list.',
        latestRun: 'Latest run',
        noRuns: 'This organisation has not been seeded yet.',
        inProgress: 'Seeding is in progress. This page updates by itself.',
        requestedBy: 'Started by {{name}}',
        summary: '{{people}} people, {{roles}} roles, {{assessments}} assessments, {{plans}} action plans',
        status_queued: 'Queued',
        status_running: 'Running',
        status_completed: 'Completed',
        status_failed: 'Failed',
        stage_clearing: 'Clearing existing data',
        stage_settings: 'Applying settings',
        stage_content: 'Writing content',
        stage_roles: 'Choosing roles',
        stage_expectations: 'Writing expectations',
        stage_details: 'Writing examples and questions',
        stage_people: 'Creating people',
        stage_history: 'Creating history',
        stage_finishing: 'Finishing',
    },

    actionPlanTester: {
        title: 'Action Plan Tester',
        description: 'Test and preview AI-generated action plans for any staff member. Inspect prompts, compare results side-by-side, and optionally apply changes.',
        selectUser: 'Select Staff Member',
        selectUserPlaceholder: 'Search for a staff member...',
        selectIndicator: 'Select Self-assessment',
        selectIndicatorPlaceholder: 'Choose a self-assessment instance',
        noIndicators: 'No self-assessments available',
        noIndicatorsHelp: 'This staff member has no self-assessment responses to work from. They need to complete at least one self-assessment before you can test action plan generation.',
        mode: 'Generation Mode',
        modeDirect: 'Direct',
        modeSuggested: 'Targeted',
        aiContextOverride: 'AI Context Override',
        aiContextOverridePlaceholder: 'Enter custom AI context to override the role default...',
        aiContextOverrideHelp: 'Leave empty to use the default context from the business role. This override is only used for this test session.',
        resetContext: 'Reset to Default',
        inspectPrompt: 'Inspect Prompt',
        generatePreview: 'Generate Preview',
        applyChanges: 'Apply Changes',
        generating: 'Generating...',
        inspecting: 'Inspecting...',
        applying: 'Applying...',
        currentPlan: 'Current Action Plan',
        previewResult: 'Preview Result',
        noPlan: 'No action plan exists yet.',
        noPreview: 'No preview generated yet. Select a user and self-assessment, then click "Generate Preview".',
        promptInspector: 'Prompt Inspector',
        promptVariables: 'Prompt Variables',
        systemInstructions: 'System Instructions',
        resolvedPrompt: 'Resolved Prompt',
        jsonSchema: 'JSON Schema',
        sessionHistory: 'Session History',
        noHistory: 'No generations in this session yet.',
        generatedAt: 'Generated at',
        timeTaken: 'Time taken',
        seconds: 'seconds',
        applied: 'Applied',
        previewOnly: 'Preview only',
        hasExistingPlan: 'Has existing plan',
        noExistingPlan: 'No existing plan',
        applySuccess: 'Action plan applied successfully.',
        applyConfirm: 'This will replace the current action plan for this self-assessment. Continue?',
        actions: 'Actions',
        expectation: 'Expectation',
        question: 'Question',
        answer: 'Answer',
        businessRole: 'Business Role',
        estimatedCompliance: 'Estimated compliance',
        fixedActions: 'Fixed Actions',
        promptEditor: 'Prompt Template Editor',
        promptEditorHelp: 'Edit the prompt template and system instructions below. Use {variableName} placeholders. Changes only apply to this test session, they are not saved to the database.',
        promptTemplate: 'Prompt Template',
        promptTemplatePlaceholder: 'Click "Inspect Prompt" first to load the current template, then edit it here...',
        systemInstructionsOverride: 'System Instructions Override',
        systemInstructionsPlaceholder: 'Click "Inspect Prompt" first to load the current instructions, then edit here...',
        resetOverrides: 'Reset to Default',
        pinResult: 'Pin for Compare',
        unpinResult: 'Unpin',
        pinnedResult: 'Pinned Result',
        latestResult: 'Latest Result',
        model: 'Model',
    },

    // Translations page
    translationsPage: {
        title: 'Translation Overrides',
        description: 'Override default translations for your organisation. Changes will take effect immediately for all users after they reload the application.',
        searchPlaceholder: 'Search translation keys or values...',
        noResults: 'No translations found matching your search.',
        key: 'Translation Key',
        defaultValue: 'Default Value',
        override: 'Override Value',
        overridePlaceholder: 'Enter custom text (leave empty to use default)',
        save: 'Save',
        saving: 'Saving...',
        saved: 'Translation saved',
        deleted: 'Override removed',
        failedToSave: 'Failed to save translation',
        failedToLoad: 'Failed to load translations',
        clearOverride: 'Clear Override',
        hasOverride: 'Has custom override',
        showOnlyOverrides: 'Show only overrides',
        totalKeys: '{{count}} translation keys',
        overrideCount: '{{count}} override(s) active',
        language: 'Language',
        pageInfo: 'Page {{current}} of {{total}} (showing {{showing}} of {{of}})',
        previous: 'Previous',
        next: 'Next',
    },

    // Email Templates page
    emailTemplates: {
        title: 'Email Templates',
        sidebarLabel: 'Email Templates',
        description: 'Customise the content of emails sent by the platform. Changes apply to your organisation only.',
        templateStaffInvite: 'Staff Member Invite',
        templateStaffAdded: 'Staff Member Added to Organisation',
        templateChatbotQuotaWarning: 'Chatbot Quota Warning (Admin)',
        templateCompetencyTestReady: 'Competency Test Ready',
        templateValidationDue: 'Indicator Submitted for Validation',
        templateIndicatorRejected: 'Indicator Rejected',
        templateActionPlanAvailable: 'Action Plan Available',
        templateSuggestedActionsAvailable: 'Action Suggestions Available',
        customised: 'Customised',
        default: 'Default',
        editTemplate: 'Edit Template',
        backToList: 'Back to Templates',
        language: 'Language',
        section: 'Section',
        defaultText: 'Default Text',
        customText: 'Custom Text',
        customTextPlaceholder: 'Enter custom text (leave empty to use default)',
        variables: 'Variables',
        variablesHelp: 'These variables will be replaced with actual values when the email is sent.',
        preview: 'Preview',
        updatePreview: 'Update Preview',
        loadingPreview: 'Loading Preview...',
        previewDescription: 'Preview shows how the email will look with your customisations applied.',
        save: 'Save Customisations',
        saving: 'Saving...',
        savedSuccessfully: 'Email template customisations saved successfully.',
        failedToSave: 'Failed to save email template customisations.',
        resetToDefault: 'Reset to Default',
        resetConfirmTitle: 'Reset to Default',
        resetConfirmMessage: 'Are you sure you want to reset this template to the default content? Your customisations for this language will be removed.',
        resetSuccessfully: 'Email template reset to default.',
        failedToReset: 'Failed to reset email template.',
        failedToLoad: 'Failed to load email template.',
        failedToLoadPreview: 'Failed to load preview.',
        noCustomisations: 'No customisations yet. The default content will be used.',
    },

    // Fixed Actions page
    fixedActionsPage: {
        description: '{{label}} are mandatory actions that will always be included in Suggested Actions after the {{indicatorLabel}} has been completed and validated. They can apply to all roles or to a specific business role.',
        allRoles: 'All Roles',
        byRole: 'By Role',
        role: 'Role',
        selectRolePlaceholder: 'Select a role to define {{label}} specific to that role',
        allRolesDescription: 'The {{label}} listed here will be applied to everybody when their Personalized Actions are generated, regardless of role. To create custom {{label}} per role, click on the "By Role" tab.',
        selectRoleToView: 'Please select a role to view {{label}} specific to that role.',
        selectRoleToAdd: 'Please select a role to add a {{label}} specific to that role.',
        because: 'Because:',
        riskIfNotMet: 'Risk if not met:',
        successCriteria: 'Success Criteria:',
        examples: 'Examples:',
        deleteConfirmTitle: 'Delete {{label}}',
        deleteConfirmMessage: 'Are you sure you want to delete this {{label}}? This will not be removed from any existing {{actionPlanLabel}} if it has already been used.',
        noActionsFound: 'No {{label}} found',
        noActionsFoundForRole: 'No {{label}} found for the role you selected. Add a {{labelSingular}} using the form on this page.',
        editLabel: 'Edit {{label}}',
        addLabelForRole: 'Add {{label}} for {{roleName}}',
        addLabelForAllRoles: 'Add {{label}} for All Roles',
        selectRoleOrAllRoles: 'Select a role (or choose All Roles)',
        willBeAppliedForRole: 'This {{label}} will be applied whenever Personalized Actions are generated for the role {{roleName}}.',
        willBeAppliedRegardless: 'This {{label}} will be applied whenever Personalized Actions are generated regardless of role.',
        created: '{{label}} created',
        updated: '{{label}} updated',
        failedToCreate: 'Failed to create {{label}}',
        failedToUpdate: 'Failed to update {{label}}',
        failedToLoad: 'Failed to load {{label}}',
        deleted: 'Deleted',
        failedToDelete: 'Failed to delete',
        fillRequiredFields: 'Please fill in all required fields',
    },

    // Company Setup page
    companySetup: {
        title: 'Company Setup',
        description: 'Basic company information is collected within this form to personalise the experience for the company.',
        companyName: 'Company Name',
        industry: 'Industry',
        employees: 'Employees',
        examplesRequired: 'Examples required when compliant',
        failedToLoad: 'Failed to load company information',
        noTenantSelected: 'No tenant selected',
        savedSuccessfully: 'Company information saved successfully',
        saveButton: 'Save Company Information',
        strategicObjectives: {
            label: 'Strategic Objectives',
            placeholder: 'Enter your company\'s strategic objectives...',
            help: 'The company\'s strategic objectives are used when generating AI role descriptions, connecting each role to the organisation\'s goals.',
        },
        defaultLanguage: {
            label: 'Default Language',
            help: 'The default language used for emails and notifications sent to staff members.',
        },
        // CRE Labels
        creLabel: 'CRE Label',
        creSingularLabel: 'Critical Role Expectation Label',
        crePluralAbbr: 'CREs (plural) Label',
        crePluralLabel: 'Critical Role Expectations (plural) Label',
        // Other Labels
        indicatorLabel: 'Self-assessment Label',
        indicatorPluralLabel: 'Self-assessments (plural) Label',
        actionPlanLabel: 'Action Plan Label',
        actionPlanPluralLabel: 'Action Plans (plural) Label',
        fixedActionLabel: 'Fixed Action Label',
        fixedActionPluralLabel: 'Fixed Actions (plural) Label',
        placeholders: {
            companyName: 'Enter company name',
            industry: 'Enter company industry',
            employeeCount: 'Select employee count',
            examplesCount: 'Select number of examples',
        },
        validation: {
            nameRequired: 'Company name is required',
            nameMinLength: 'Name must be at least 2 characters',
            industryRequired: 'Industry is required',
            employeesRequired: 'Employee count is required',
            examplesRequired: 'Examples required when compliant is required',
            fieldRequired: 'This field is required',
            minLength2: 'Must be at least 2 characters',
            minLength1: 'Must be at least 1 character',
        },
        indicatorSettings: {
            title: 'Self-assessment Question Settings',
            questionType: 'Question Type',
            questionTypeHelp: 'Choose how staff respond to self-assessments',
            evidenceWithExamples: 'Evidence with Examples (Full)',
            yesOrNo: 'Yes/No (Simplified)',
            yesLabel: 'Custom "Yes" Label',
            yesLabelPlaceholder: 'Leave blank for default "Yes"',
            noLabel: 'Custom "No" Label',
            noLabelPlaceholder: 'Leave blank for default "No"',
            complianceDerivedYesNo: 'Derive Answer from Examples',
            complianceDerivedYesNoHelp: 'Yes/No is automatically determined by whether all 5+5 examples are checked',
            hideDerivedYesNo: 'Hide Yes/No from Staff Members',
            hideDerivedYesNoHelp: 'Staff Members only see checkboxes; they won\'t know their response is being evaluated as yes/no',
            managerVerification: 'Manager Verification',
            managerVerificationHelp: 'When enabled, managers must verify staff submissions before action plans are generated',
            excludeCalibrateStaff: 'Exclude Calibrate Staff from Reports',
            excludeCalibrateStaffHelp: 'When enabled, staff members with @calibratelearning.com or @calibratetechnology.com email addresses will be excluded from all reports',
            actionPlanBehaviour: 'Action Plan Behaviour',
            actionPlanBehaviourHelp: 'Choose how action plans work for staff members after completing self-assessment',
            actionPlanReview: 'Manager Review',
            actionPlanReviewHelp: 'Managers record a Met / Not Met review against each action and overall, and staff add their own comments',
            previousAnswerReuse: 'Re-use Previous Answers',
            previousAnswerReuseHelp: 'Staff can see their answers from previous self-assessments and re-use them as a starting point',
            answerComparison: 'Compare With Previous Answers',
            answerComparisonHelp: 'When verifying, managers see how similar each answer is to last time, with an AI summary of what changed',
            reportMinGroupSize: 'Minimum Group Size in Summaries',
            reportMinGroupSizeHelp: 'In aggregated summaries (e.g. for Governors), groups with fewer staff than this are combined so individuals cannot be identified',
            actionPlanModeDisabled: 'Action Plans Disabled',
            actionPlanModeSuggestionsFirst: 'Suggestions First',
            actionPlanModeNoSuggestions: 'No Suggestions, straight to Action Plan',
            actionPlanSuggestions: 'Your Action Plan',
            requireConfirmation: 'Require Answer Confirmation',
            requireConfirmationHelp: 'When enabled, staff members must confirm each answer on the review page before they can submit their indicator',
        },
        competencyTesting: {
            title: 'Competency Exercise Testing',
            description: 'Test staff member competency based on their self-assessment responses. When enabled, staff who answer "Yes" to expectations will receive follow-up competency exercises',
            enabled: 'Enable Competency Exercise Testing',
            enabledHelp: 'Generate competency exercises for "Yes" answers',
            autoSend: 'Auto-send Competency Exercises',
            autoSendHelp: 'Automatically send competency exercises when self-assessments are submitted',
            delayMinutes: 'Delay (minutes)',
            delayMinutesHelp: 'How long to wait after submission before sending the competency exercise (0 = immediate)',
            taskQuestionsPerExpectation: 'Task Questions per Role Expectation',
            taskQuestionsPerExpectationHelp: 'Max task-type questions per Role Expectation. Leave blank for all.',
            behaviourQuestionsPerExpectation: 'Behaviour Questions per Role Expectation',
            behaviourQuestionsPerExpectationHelp: 'Max behaviour-type questions per Role Expectation. Leave blank for all.',
            allQuestions: 'All',
            showScoresToStaff: 'Show Scores to Staff Members',
            showScoresToStaffHelp: 'When enabled, staff members can see their competency exercise scores and answers. When disabled, only managers can view results.',
            taskThreshold: 'Task Checkbox Threshold',
            taskThresholdHelp: 'Number of task checkboxes that must be checked (0–5) for a "Yes" answer. Default 5 = all must be checked.',
            behaviourThreshold: 'Behaviour Checkbox Threshold',
            behaviourThresholdHelp: 'Number of behaviour checkboxes that must be checked (0–5) for a "Yes" answer. Default 5 = all must be checked.',
        },
        simulationExercise: {
            title: 'Simulation Exercise',
            description: 'Enable simulation exercise functionality for your organization.',
            enabled: 'Enable Simulation Exercise',
            enabledHelp: 'When enabled, simulation exercise menu will be available',
        },
        workflowPreview: {
            title: 'Staff Workflow',
            description: 'This shows the workflow steps that staff will follow based on the options you have enabled above. Toggle settings to see how the workflow changes.',
        },
        workflowBuilder: {
            title: 'Workflow Steps',
            description: 'Configure and reorder the optional steps in your staff workflow. Drag to reorder, toggle to enable or disable.',
            reviewCres: 'Review CREs',
            completeIndicator: 'Complete Indicator',
            verification: 'Manager Verification',
            fixedStep: 'Fixed step — always present',
            competencyTest: 'Competency Test',
            competencyTestDesc: 'Test staff member competency based on their indicator responses',
            simulationExercise: 'Simulation Exercise',
            simulationExerciseDesc: 'Practical simulation exercise for staff',
            actionPlan: 'Action Plan',
            actionPlanDesc: 'Staff members create action plans based on their self-assessment results',
            competencyRequiresYesNo: 'Competency testing requires Yes/No indicator type',
        },
        chatbot: {
            title: 'Calibrate AI Assistant Settings',
            resetDay: 'Monthly Reset Day',
            resetDayHelp: 'Day of the month when Calibrate AI Assistant message quotas reset',
        },
        reports: {
            title: 'Reports',
            showComingSoon: 'Show "Coming Soon" Reports',
            showComingSoonHelp: 'When enabled, reports that are not yet available will appear in the Reports section with a "Coming Soon" label',
        },
    },

    // AI Setup page
    aiSetup: {
        title: 'AI Setup',
        description: 'Configure the AI assistant settings for your tenant.',
        selectTenantMessage: 'Please select a tenant to configure AI settings.',
        openaiConfig: 'OpenAI Configuration',
        assistantId: 'Assistant ID',
        vectorStoreId: 'Vector Store ID',
        systemInstructions: 'System Instructions',
        suggestedActionsCount: 'Personalized Actions Count',
        failedToLoad: 'Failed to load AI configuration',
        savedSuccessfully: 'AI configuration saved successfully',
        saveButton: 'Save AI Configuration',
        placeholders: {
            assistantId: 'Enter OpenAI Assistant ID (optional)',
            systemInstructions: 'Enter system instructions for the AI assistant (optional)',
        },
        validation: {
            minimum0: 'Minimum 0',
            maximum5: 'Maximum 5',
        },
        chatbotAccess: {
            title: 'Calibrate AI Assistant Access & Limits',
            accessLevel: 'Access Level',
            accessLevelHelp: 'Control which user roles can access the AI Assistant',
            levels: {
                off: 'Off - No one can access',
                internal: 'Internal Only - Admins only',
                internalAndManagers: 'Internal & Managers',
                all: 'All Users',
            },
            monthlyLimits: 'Monthly Message Limits',
            monthlyLimitsHelp: 'Set the maximum number of messages each role can send per month. Set to 0 for unlimited.',
            limitInternal: 'Admin Limit',
            limitManager: 'Manager Limit',
            limitStaff: 'Staff Limit',
            unlimitedHint: '0 = unlimited',
        },
        vectorStoreManaged: 'Created automatically when the first document is uploaded below.',
        documents: {
            title: 'Assistant Documents',
            description: 'Upload policies, handbooks and guides. The Calibrate Assistant searches them when a question is relevant and shows which documents it used.',
            upload: 'Upload documents',
            supportedTypes: 'PDF, Word, PowerPoint, text, Markdown or HTML',
            search: 'Search documents',
            noMatches: 'No documents match your search.',
            empty: 'No documents uploaded yet.',
            delete: 'Delete',
            confirmDelete: 'Delete this document? The assistant will no longer be able to search it.',
            failedToUpload: 'Could not upload {{name}}',
            failedToDelete: 'Could not delete the document',
            status: {
                indexing: 'Indexing',
                ready: 'Ready',
                failed: 'Failed',
            },
        },
        restrictions: {
            title: 'Calibrate AI Assistant Restrictions',
            description: 'Define rules that the Calibrate AI Assistant must follow. These restrictions are included in the chatbot\'s system prompt but are not visible to users.',
            addPlaceholder: 'Enter a new restriction (e.g., "Do not discuss competitor products")',
            add: 'Add',
            noRestrictions: 'No restrictions defined yet',
            addedSuccessfully: 'Restriction added',
            deletedSuccessfully: 'Restriction deleted',
            failedToAdd: 'Failed to add restriction',
            failedToDelete: 'Failed to delete restriction',
        },
        ratingMode: {
            title: 'Self-assessment Rating Mode',
            description: 'Choose how employee answers are coached during self-assessments. RAG provides a simple Red/Amber/Green rating, while STAR uses the Situation-Task-Action-Result methodology to provide deeper coaching with suggestions and questions.',
            label: 'Rating Mode',
            help: 'This affects how staff receive feedback on their self-assessment answers.',
            modes: {
                disabled: 'Disabled (No Coaching)',
                rag: 'RAG (Red / Amber / Green)',
                star: 'STAR (Situation / Task / Action / Result)',
            },
        },
    },

    // Prompt Customiser page
    promptCustomiser: {
        title: 'Prompt Customiser',
        sidebarLabel: 'Prompt Customiser',
        description: 'Customise the AI prompts used by the platform. Each prompt controls a specific AI behaviour. Override the default to tailor it to your organisation, or reset to revert.',
        loading: 'Loading prompts…',
        noPrompts: 'No prompts are available for your current configuration.',
        failedToLoad: 'Failed to load prompts',
        savedSuccessfully: 'Prompt override saved successfully',
        failedToSave: 'Failed to save prompt override',
        resetSuccessfully: 'Prompt reset to default',
        failedToReset: 'Failed to reset prompt',
        customised: 'Customised',
        default: 'Default',
        message: 'Prompt Message',
        messagePlaceholder: 'Enter the prompt message…',
        model: 'AI Model',
        save: 'Save Override',
        resetToDefault: 'Reset to Default',
        resetConfirm: 'Are you sure you want to reset this prompt to the default? Your custom override will be deleted.',
        showDefault: 'Show Default',
        hideDefault: 'Hide Default',
        defaultMessage: 'Default Message',
        defaultModel: 'Default Model',
        variables: 'Variables',
        variablesHelp: 'These are the variables the default prompt uses, so they\'re what the platform supplies. Add one to the prompt by typing its name in curly brackets, for example {role}. The company name, industry and size, your organisation\'s AI instructions and its uploaded documents are always included automatically.',
        variablesNone: 'The default prompt doesn\'t use any variables.',
        variableUsed: 'Used in this prompt',
        variableMissingTitle: 'Used by the default prompt but missing from this one',
        variablesMissing: 'Missing from this prompt: {{list}}. The default prompt uses these, so the AI won\'t get this information unless you add them back.',
        variablesExtra: 'Not used by the default prompt: {{list}}. Check the spelling. If the platform doesn\'t supply a variable, it reaches the AI as literal text.',
        variableIssues_one: '{{count}} variable issue',
        variableIssues_other: '{{count}} variable issues',
    },

    // Chatbot Usage Report page
    chatbotUsage: {
        title: 'Calibrate AI Assistant Usage',
        description: 'Monitor AI Assistant usage across your organization and manage user limits.',
        noTenant: 'Please select a tenant to view AI Assistant usage.',
        failedToLoad: 'Failed to load usage data',
        noUsers: 'No users found',
        unlimited: 'Unlimited',
        setOverride: 'Set Override',
        overrideSaved: 'Override saved successfully',
        overrideFailed: 'Failed to save override',
        overrideCleared: 'Override cleared successfully',
        overrideClearFailed: 'Failed to clear override',
        userUsage: 'User Usage',
        stats: {
            activeUsers: 'Active Users',
            outOf: 'out of {{total}} total',
            messagesUsed: 'Messages Used',
            ofAvailable: 'of {{total}} available',
            utilization: 'Utilization',
            nextReset: 'Next Reset',
        },
        filters: {
            period: 'Period',
            periods: {
                current: 'Current Period',
                lastMonth: 'Last Month',
                last3Months: 'Last 3 Months',
                last6Months: 'Last 6 Months',
            },
            sortBy: 'Sort By',
            sorts: {
                name: 'Name',
                mostActive: 'Most Active',
                leastActive: 'Least Active',
            },
        },
        table: {
            user: 'User',
            role: 'Role',
            used: 'Used',
            limit: 'Limit',
            remaining: 'Remaining',
            actions: 'Actions',
        },
        roles: {
            internal: 'Admin',
            manager: 'Manager',
            staff: 'Staff',
        },
        overrideModal: {
            title: 'Set Temporary Override',
            description: 'Set a temporary message limit override for {{name}}. This override will expire at the end of the current billing period.',
            currentLimit: 'Current Limit',
            hasOverride: 'has active override',
            newLimit: 'New Limit',
            newLimitHelp: 'Set to 0 for unlimited messages this period',
            clearOverride: 'Clear Override',
            save: 'Save Override',
        },
    },

    // Professional Standards page
    professionalStandards: {
        title: 'Professional Standards',
        ensureTitles: 'Please ensure all professional standards have titles.',
        addAtLeastOne: 'Please add at least one professional standard before saving',
        savedSuccessfully: 'Professional standards saved successfully!',
        deleteHeader: 'Delete this professional standard?',
        deleteMessage: 'Are you sure you want to delete this professional standard and all its expectations? This action cannot be undone.',
        addNew: 'Add New Professional Standard',
        titleLabel: 'Professional Standard Title',
        titlePlaceholder: 'Enter professional standard title...',
        descriptionLabel: 'Description (Optional)',
        descriptionPlaceholder: 'Enter description for this professional standard...',
        expectationsLabel: 'Expectations for this Standard:',
        expectationPlaceholder: 'Expectation {{number}}',
        addExpectation: 'Add Expectation',
        deleteStandard: 'Delete Standard',
    },

    // Survey / Indicator questions
    survey: {
        rateYourself: 'Rate Yourself',
        provideEvidence: 'Provide Evidence',
        provideExamples: 'Provide Examples',
        addComment: 'Add Comment',
        submitForReview: 'Submit for Review',
        aiGuidance: 'AI Guidance',
        starCoaching: 'STAR Coaching',
        starCoachingDescription: 'Automated AI coaching that helps you describe the <strong>S</strong>ituation, <strong>T</strong>ask, <strong>A</strong>ction and <strong>R</strong>esult, contributing to an effective example.',
        suggestions: 'Suggestions',
        questionsToConsider: 'Questions to consider',
        needsImprovement: 'Needs improvement',
        ok: 'OK',
        good: 'Good',
    },

    // Comments
    comments: {
        title: 'Comments',
        comment: 'Comment',
        addComment: 'Add Comment',
        editComment: 'Edit Comment',
        deleteComment: 'Delete Comment',
        deleteCommentConfirm: 'Are you sure you want to delete this comment? This action cannot be undone.',
        addCommentToIndicator: "Add comment to {{name}}'s {{indicatorLabel}}",
        updateCommentOnIndicator: "Update comment on {{name}}'s {{indicatorLabel}}",
        managerAddedComment: '{{name}} added comment ({{date}})',
        selectExpectation: "Select the expectation you'd like to comment on from the list below. They are grouped by Task expectations, and then by Behaviour and attitude.",
        respectExpectations: 'Respect Expectations',
        taskExpectations: 'Task Expectations',
        errorLoadingReports: 'Error loading all reports',
        userProfile: 'User Profile',
        expectationChosenIs: 'The expectation that you have chosen to comment on is...',
        commentPlaceholder: 'Please enter your comment here. Make your comment as fair, reasonable and specific as possible.',
        updateComment: 'Update Comment',
    },

    // Time/Date related
    time: {
        today: 'Today',
        yesterday: 'Yesterday',
        thisWeek: 'This Week',
        lastWeek: 'Last Week',
        thisMonth: 'This Month',
        lastMonth: 'Last Month',
        daysAgo: '{{count}} days ago',
        hoursAgo: '{{count}} hours ago',
        minutesAgo: '{{count}} minutes ago',
        justNow: 'Just now',
    },

    // Footer
    footer: {
        termsOfUse: 'Terms of Use',
        privacyPolicy: 'Privacy Policy',
        enterpriseEdition: 'Enterprise Edition',
    },

    // Chat / AI Assistant
    chat: {
        scrollToBottom: 'Scroll to bottom',
        welcomeMessage: 'How can I help you today?',
        writeMessage: 'Write a message...',
        send: 'Send',
        copy: 'Copy',
        quotaRemaining: '{{remaining}} of {{limit}} messages remaining this period',
        quotaLow: 'Only {{remaining}} messages remaining this period',
        quotaExceeded: 'Message limit reached for this period',
        history: 'History',
        newChat: 'New chat',
        untitledChat: 'New chat',
        loadingConversation: 'Loading conversation',
        messageInput: 'Message',
        stopGenerating: 'Stop generating',
        cancelSending: 'Cancel sending',
        regenerate: 'Regenerate',
        working: 'Assistant is working',
        reasoning: 'Reasoning',
        attachment: 'Attachment',
        attachmentImage: 'Image',
        attachmentDocument: 'Document',
        attachmentPreview: 'Preview {{name}}',
        attachmentTooLarge: 'Files must be 20 MB or smaller',
        addAttachment: 'Attach a file',
        removeAttachment: 'Remove {{name}}',
        uploading: 'uploading',
        uploadFailed: 'Upload failed',
        searchThreads: 'Search conversations',
        noThreadsFound: 'No conversations found',
        loadingThreads: 'Loading conversations',
        today: 'Today',
        yesterday: 'Yesterday',
        earlier: 'Earlier',
        running: 'Running',
        renameThread: 'Rename conversation',
        moreOptions: 'More options',
        rename: 'Rename',
        archive: 'Archive',
        delete: 'Delete',
        close: 'Close',
        backToChat: 'Back to conversation',
        greeting: 'Hi {{name}}, how can I help?',
        greetingSubtitle: 'Ask me about your role, your {{cre}}, your {{actionPlan}} or your organisation\'s documents.',
        actionPlanFallback: 'Action Plan',
        indicatorFallback: 'Indicator',
        quotaResets: 'Resets on {{date}}',
        suggestions: {
            cre: {
                title: 'What\'s in my {{cre}}?',
                label: 'My Respect and Task expectations',
                prompt: 'Explain the contents of my {{cre}}, including my Respect and Task expectations.',
            },
            actionPlan: {
                title: 'Help with my {{actionPlan}}',
                label: 'What to focus on next',
                prompt: 'Help me make progress on my current {{actionPlan}}. What should I focus on first?',
            },
            indicator: {
                title: 'Prepare for my next {{indicator}}',
                label: 'What to expect and how to get ready',
                prompt: 'How should I prepare for my next {{indicator}}?',
            },
            help: {
                title: 'How can you help me?',
                label: '',
                prompt: 'How can you help me?',
            },
        },
        tools: {
            fileSearch: 'Searched your organisation\'s documents',
            actionPlan: 'Checked the action plan',
            indicatorStatus: 'Checked the indicator schedule',
            indicatorDetail: 'Read the indicator answers',
            indicators: 'Looked through past indicators',
            workflowStatus: 'Checked progress through the workflow',
            expectationGuidance: 'Read the guidance on expectations',
            competencyResults: 'Checked competency test results',
            teamOverview: 'Checked on your team',
            generic: 'Used {{name}}',
        },
    },

    // Compliance / Action Plan status
    compliance: {
        thisStaffMember: 'This staff member',
        noActionPlanYet: 'No {{actionPlanLabel}} yet',
        suggestedActionsCreated: 'Personalized Actions Created',
        actionPlanCreated: '{{actionPlanLabel}} Created',
        noActionPlansDescription: 'No {{actionPlanLabelPlural}} have been generated yet. They are generated once {{personName}} has submitted their {{indicatorLabel}} and it has been validated by their line manager.',
        noActionPlansDescriptionNoVerification: 'No {{actionPlanLabelPlural}} have been generated yet. They are generated once {{personName}} has submitted their {{indicatorLabel}}.',
        suggestedActionsDescription: 'Suggested actions were created for the latest {{indicatorLabel}}. Awaiting {{personName}} to save their {{actionPlanLabel}}.',
        actionPlanCreatedDescription: 'The latest {{actionPlanLabel}} has been created for the most recent {{indicatorLabel}}.',
        checkingStatus: 'Checking the latest {{actionPlanLabel}} status…',
        viewSuggestedActions: "View {{name}} personalized actions",
        viewLatestActionPlan: "View {{name}} latest {{actionPlanLabel}}",
    },

    // Demo pages
    demo: {
        title: 'Demo',
        ai: {
            title: 'CalAi Demonstration',
            pageTitle: 'CalAi Task Expectations Demonstration',
            intro: 'Calibrate Technology uses the power of AI to bring efficiencies to managers and ensure that their teams remain competent and compliant.',
            instructions: 'In this demonstration, you will enter a Job Role, and Calibrate will return a list of 10 Task Expectations that must be met by a person in that role.',
            generateTitle: 'Generate Task Expectations',
            generateInstructions: "Enter a job role below that you'd like to see some Task Expectations for. We will generate the first 10.",
            enterJobRole: 'Enter job role here',
            placeholderAviation: '(e.g. Ramp Agent, Warehouse Supervisor)',
            placeholderEducation: '(e.g. Academic Advisor, Examination Officer)',
            expectationsFor: 'Task Expectations for {{role}}',
            serverError: 'There was an issue communicating with the server. Are you connected to the Internet?',
        },
        pulse: {
            title: 'Pulse Demonstration',
            pageTitle: 'Safety Pulse Demonstration',
            youScored: 'You scored...',
            thisIsDemo: 'This is a demonstration.',
            scoreResult: 'Based on your answers to this safety pulse demonstration, you scored {{score}}/{{maxScore}}.',
            answers: {
                exceptional: 'Exceptional practice and full support from all management and employees alike.',
                aboveExpected: 'Above industry expected levels of practice',
                asExpected: 'As expected industry levels of practice',
                belowExpected: 'Below industry levels of practice',
                highRisk: 'Risk and high reason for concern as industry expected levels are in no way being practiced on a regular and consistent level',
            },
            questions: {
                q1: 'Communicate and enforce the safety policy within the terminal',
                q2: 'Ensure all staff members are aware of safety objectives.',
                q3: 'Implement safety controls at the operational level.',
                q4: 'Conduct regular safety drills and exercises.',
                q5: 'Monitor safety compliance within the operation',
                q6: 'Address any identified safety issues promptly',
                q7: 'Establish a (SAG) safety  Action Group that relates to operation',
                q8: 'Conduct regular safety audits and inspections',
                q9: 'Foster open communication channels for reporting safety concerns',
                q10: 'Implement a safety reporting system for staff',
            },
        },
        survey: {
            title: 'Survey',
            pageTitle: 'SMS Awareness & Compliance Survey',
            completionMessage: 'This is a demonstration. At this point the survey will be submitted for review and/or automated review.',
        },
    },

    // Updates
    updates: {
        updateDone: 'Update done',
        noUpdateAvailable: 'No update available',
        performUpdate: 'Perform Update',
        mobileOnlyFeature: 'This feature only works on a mobile device when installed from the App Store.',
    },

    // Legal
    legal: {
        termsOfUse: 'Terms of Use',
        privacyPolicy: 'Privacy Policy',
    },

    // Competency Questions Editor
    competencyQuestionGeneration: {
        title: 'Generate competency questions',
        intro: 'Generate new competency questions for several roles at once. It runs in the background, so you can leave this page. The questions of the chosen types are replaced for every {{creAbbr}} in the selected roles; past competency tests keep the questions they used.',
        questionTypes: 'Question types',
        type: {
            task: 'Task',
            behaviour: 'Behaviour',
        },
        typesInSentence: {
            task: 'task',
            behaviour: 'behaviour',
            both: 'task and behaviour',
        },
        roles: 'Roles',
        selectAll: 'Select all',
        deselectAll: 'Deselect all',
        noRoles: 'There are no roles yet.',
        noExpectations: 'No {{creAbbrPlural}} yet',
        roleCounts: '{{expectations}} {{creAbbrPlural}} · {{task}} task questions · {{behaviour}} behaviour questions',
        generate: 'Generate questions',
        confirmHeader: 'Replace questions?',
        confirmMessage: 'The {{types}} questions for {{roles}} roles will be replaced with new AI-generated ones ({{count}} sets of 5 questions). This cannot be undone.',
        started: 'Generation has started. It runs in the background.',
        alreadyRunning: 'A generation run is already in progress.',
        startError: 'Could not start generation. Please try again.',
        loadError: 'Could not load roles. Please try again.',
        inProgress: 'Generating questions: {{done}} of {{total}} {{creAbbrPlural}} done',
        score: '{{score}} pts',
        lastRun: 'Last run on {{date}}: {{done}} of {{total}} sets of questions generated, across {{roles}} roles',
        viewDetails: 'View details',
        detailsTitle: 'Generated questions',
        roleSummary: '{{done}} of {{total}} sets generated',
        replacedSince: 'replaced since',
        itemFailed: 'Not generated. The existing questions were kept.',
        itemPending: 'Waiting to be generated…',
        deletedRole: 'Deleted role',
        deletedExpectation: 'Deleted {{creAbbr}}',
        failed_one: '{{count}} could not be generated. Their existing questions were kept; you can run them again.',
        failed_other: '{{count}} could not be generated. Their existing questions were kept; you can run them again.',
    },
    competencyQuestions: {
        title: 'Competency Questions',
        description: 'Add multiple-choice questions to test competency for this expectation. Users who answer "Yes" to this expectation will receive these questions.',
        expectation: 'Expectation',
        noQuestions: 'No competency questions have been added yet.',
        addQuestion: 'Add Question',
        question: 'Question',
        questionCode: 'Question Code',
        questionText: 'Question Text',
        questionTextPlaceholder: 'Enter the question...',
        questionTextRequired: 'Question text is required',
        questionType: 'Question Type',
        questionTypePlaceholder: 'Select question type...',
        questionTypeUnspecified: 'Unspecified',
        questionTypeTask: 'Task',
        questionTypeBehaviour: 'Behaviour',
        options: 'Answer Options',
        optionTextPlaceholder: 'Enter option text...',
        optionTextRequired: 'All option texts are required',
        saved: 'Competency questions saved successfully',
        saveError: 'Failed to save competency questions',
        editQuestions: 'Edit Competency Questions',
        questionsCount: '{{count}} question(s)',
        addOption: 'Add Option',
        answerText: 'Answer Text',
        score: 'Score',
        minimumOptionsRequired: 'Each question must have at least 2 options',
        searchQuestions: 'Search questions...',
        filterByType: 'Filter by type',
        allTypes: 'All Types',
        showingQuestions: 'Showing {{start}}-{{end}} of {{total}} questions',
        previousPage: 'Previous',
        nextPage: 'Next',
        generate: 'Generate',
        chooseQuestionType: 'Choose Question Type',
        generateTask: 'Generate Task Questions',
        generateBehaviour: 'Generate Behaviour Questions',
        generateBoth: 'Generate Task and Behaviour Questions',
        generatePartialError: 'Some questions could not be generated. Showing the ones that were.',
        generating: 'Generating...',
        generatedQuestions: 'Generated Questions',
        selectQuestionsToAdd: 'Select which questions to add to this expectation. You can review each question and its options before adding.',
        selectAll: 'Select All',
        deselectAll: 'Deselect All',
        addSelected: 'Add Selected ({{count}})',
        noQuestionsGenerated: 'No questions were generated. Please try again.',
        generateError: 'Failed to generate questions. Please try again.',
        questionsAdded: '{{count}} question(s) added successfully',
    },

    // Competency Tests
    competencyTests: {
        title: 'Competency Exercises',
        takeTest: 'Take Competency Exercise',
        selectOneOption: 'Please select one option from the list below',
        noTests: 'No competency exercises available.',
        testStatus: 'Status',
        notStarted: 'Not Started',
        inProgress: 'In Progress',
        submitted: 'Submitted',
        startTest: 'Start Competency Exercise',
        continueTest: 'Continue Competency Exercise',
        viewResults: 'View Results',
        score: 'Score',
        maxScore: 'Max Score',
        progress: 'Progress',
        questionOf: 'Question {{current}} of {{total}}',
        submitTest: 'Submit Competency Exercise',
        confirmSubmit: 'If you want to review any question, use the "previous" button to go back.\n\nYou cannot change your answers after submission.',
        submittedSuccessfully: 'Competency Exercise submitted successfully!',
        testComplete: 'Competency Exercise Complete',
        yourScore: 'Your Score',
        testSubmittedSuccessfully: 'Competency Exercise submitted successfully!',
        managerWillReviewResults: 'Your results will now be used to create your personalised action plan and identify the support that will help you improve.',
        aboutText: 'Competency exercises assess your knowledge and skills against your role expectations.\nComplete these exercises to demonstrate your understanding and support your personal development.',
        testNotFound: 'This competency exercise could not be found.',
        errorLoadingTest: 'An error occurred while loading the competency exercise. Please try again.',
        cannotSubmitYet: 'Cannot submit competency exercise yet',
        pleaseAnswerAllQuestions: 'Please answer all competency questions before submitting.',
        unansweredQuestions: 'Unanswered questions',
        // Banner/notification strings
        testAvailable: 'Competency Exercise Available!',
        testAvailableDescription: 'You have a competency exercise with {{count}} questions waiting to be completed.',
        testInProgressTitle: 'Continue Your Competency Exercise',
        testInProgressDescription: 'You have answered {{answered}} of {{total}} questions.',
        continueTestDescription: 'Pick up where you left off on your competency exercise.',
        progressStats: '{{answered}} of {{total}} answered',
        questionCountStats: '{{count}} questions',
        viewAll: 'View All Competency Exercises',
    },

    // Reports
    // Manager review of action plans (Met / Not Met)
    actionPlanReview: {
        outcome: {
            MET: 'Met',
            NOT_MET: 'Not Met',
        },
        state: {
            NOT_STARTED: 'Not reviewed',
            IN_PROGRESS: 'Review in progress',
            COMPLETED: 'Reviewed',
        },
        review: 'Review:',
        commentPlaceholder: 'Add a comment (optional)',
        overallTitle: 'Overall {{actionPlanLabel}} Review',
        overallHelp: 'Once you have reviewed each action, give an overall review for this {{actionPlanLabel}}.',
        overallCommentPlaceholder: 'Summarise the year: strengths, progress against the objectives and next steps',
        overallChoose: 'Overall review',
        overallCommentLabel: 'Summary comment (optional)',
        readyToComplete: 'Ready to complete. {{name}} will be notified.',
        stillToDo: '{{count}} actions still to review{{overall}}',
        andOverall: ', plus the overall review',
        chooseOverall: 'All actions reviewed. Choose the overall outcome to complete the review.',
        unsavedChanges: 'Unsaved changes',
        progress: '{{reviewed}} of {{total}} actions reviewed',
        saveDraft: 'Save draft',
        complete: 'Complete review',
        reopen: 'Reopen review',
        reviewButton: 'Review {{actionPlanLabel}}',
        completedBy: 'Reviewed by {{name}} on {{date}}',
        completedOn: 'Reviewed on {{date}}',
        incompleteHeader: 'Review incomplete',
        incompleteMessage: 'Review every action, choose an overall outcome and wait for {{name}} to add their comments before completing the review.',
        confirmCompleteHeader: 'Complete review?',
        confirmCompleteMessage: '{{name}} will be notified and will be able to see your reviews and comments. You can reopen the review later if you need to make changes.',
        saved: 'Review saved',
        completed: 'Review completed',
        reopened: 'Review reopened',
        saveFailed: 'Unable to save the review. Please try again.',
        managerCommentTitle: 'Manager comments',
        staffCommentTitle: '{{name}}\'s comments',
        staffCommentMissing: '{{name}} has not added their comments yet. You can complete the review once they have.',
        awaitingStaffComment: 'Waiting for {{name}} to add their comments.',
        remind: 'Remind',
        reminded: 'Reminder sent to {{name}}',
        remindFailed: 'Unable to send the reminder. Please try again.',
        ownCommentTitle: 'Your comments',
        ownCommentHelp: 'Add your own comments on your {{actionPlanLabel}}: what went well, what was difficult and what you would like to focus on next. Your manager will see them, and can complete the review once you have added them.',
        ownCommentPlaceholder: 'Your comments',
        saveComment: 'Save comments',
        commentSaved: 'Comments saved',
        commentedOn: 'Added on {{date}}',
    },

    // Previous indicator answers: re-use while answering, comparison while verifying
    answerHistory: {
        toggle: 'Your previous answers ({{count}})',
        differentQuestion: 'Last time you rated yourself differently, so you answered a different question. These may still be a useful starting point.',
        lastRating: 'Last time you chose: {{rating}} ({{indicatorLabel}} #{{number}}, {{date}})',
        type: {
            EXPLANATION: 'Explanation',
            EVIDENCE_1: 'Example 1',
            EVIDENCE_2: 'Example 2',
            EVIDENCE_3: 'Example 3',
        },
        fromIndicator: '{{indicatorLabel}} #{{number}} · {{date}}',
        reuse: 'Re-use this answer',
        replaceHeader: 'Replace your current answer?',
        replaceMessage: 'This will replace what you have typed with your previous answer. You can still edit it afterwards.',
        replace: 'Replace',
        noPrevious: 'No previous answer to compare with.',
        similarity: '{{percent}}% similar',
        band: {
            near_identical: 'Near-identical to last time (likely copied)',
            largely_similar: 'Largely similar to last time',
            partly_reworded: 'Partly reworded since last time',
            substantially_new: 'Substantially new answer',
        },
        unchanged: 'No meaningful change from the previous answer.',
        aiLabel: 'AI-generated',
        compareWith: 'Compare with {{indicatorLabel}} #{{number}} ({{date}})',
        previousAnswer: 'Previous answer',
        changes: 'Changes',
        diffLegend: 'Highlighted text is new; struck-through text has been removed.',
    },

    reports: {
        title: 'Reports',
        error: 'Failed to load report data. Please try again.',
        noData: 'No data available',
        scope: {
            all: 'Company-wide data',
            team: 'Your team data',
        },
        filters: {
            period: 'Time Period',
            granularity: 'Granularity',
            last7d: 'Last 7 days',
            last30d: 'Last 30 days',
            last60d: 'Last 60 days',
            last90d: 'Last 90 days',
            custom: 'Custom range',
            daily: 'Daily',
            weekly: 'Weekly',
            monthly: 'Monthly',
            startDate: 'Start Date',
            endDate: 'End Date',
            apply: 'Apply',
            businessRole: 'Business Role',
            manager: 'Manager',
            allRoles: 'All Roles',
            allManagers: 'All Managers',
        },
        table: {
            name: 'Name',
            role: 'Role',
            businessRole: 'Business Role',
            users: 'Users',
        },
        export: {
            csv: 'Export CSV',
            processing: 'Exporting…',
            failed: 'Export failed',
        },
        compliance: {
            title: 'Compliance & Concern',
            titleSelfReported: 'Self-Reported Compliance',
            avgCompliance: 'Average Compliance',
            avgComplianceSelfReported: 'Avg Self-Reported',
            selfReportedBanner: 'Self-reported data — staff marked their own checkboxes when submitting their indicator. This reflects confidence, not verified performance.',
            totalUsers: 'Total Users',
            assessed: 'Assessed',
            unassessed: 'Unassessed',
            compliance: 'Compliance',
            complianceSelfReported: 'Self-Reported',
            concern: 'Concern',
            complianceTrend: 'Compliance Trend',
            concernDistribution: 'Concern Distribution',
            high: 'High Concern',
            medium: 'Medium Concern',
            low: 'Low Concern',
            byRole: 'Compliance by Role',
            userDetail: 'User Details',
        },
        indicators: {
            title: '{{indicatorLabel}} Completion',
            total: 'Total',
            submitted: 'Submitted',
            submittedCount: 'Submitted (period)',
            totalInPeriod: 'Total (period)',
            inProgress: 'In Progress',
            completionRate: '% Submitted',
            overdue: 'Overdue',
            avgSubmitTime: 'Average Submit Time',
            submissionTrend: 'Submission Trend',
            byState: 'By State',
            userDetail: 'User Details',
            staffProgress: 'Staff Progress',
            staffProgressDescription: 'Current state of each staff member\'s latest indicator in the selected period, alongside overall submission counts.',
            currentState: 'Current Status',
            latestSubmittedAt: 'Last Submitted',
            state: {
                SUBMITTED: 'Submitted',
                IN_PROGRESS: 'In Progress',
                NOT_STARTED: 'Not Started',
            },
            fivePlusFive: {
                title: '5+5 Checkbox Analytics',
                description: 'Checkbox completion across task examples for the latest submitted indicator per staff member.',
                avgCheckboxes: 'Avg Checkboxes Checked',
                totalUsers: 'Users',
                allChecked: 'All Checked',
                noneChecked: 'None Checked',
                distribution: 'Checkbox Distribution',
                usersCount: 'Users',
                examples: 'Task Examples',
                exampleLabel: 'Task Description',
                checkedCount: 'Staff Checked (count)',
                totalResponses: 'Total Respondents',
                checkedRate: 'Self-Reported Rate',
                checkedRateExplainer: 'A high rate means most staff claim competence in this area. A low rate flags a recognised gap. Compare against overall self-reported compliance to spot potential over- or under-reporting.',
                exampleType: 'Type',
                typeTask: 'Task',
                typeBehaviour: 'Behaviour',
                checkedByLabel: 'Checked by',
                notCheckedByLabel: 'Not checked by',
                noUsers: 'No users',
            },
        },
        verification: {
            title: 'Verification Pipeline',
            pending: 'Pending',
            overdue: 'Overdue',
            completed: 'Completed',
            rejected: 'Rejected',
            approvalRate: 'Approval Rate',
            avgTime: 'Average Verification Time',
            verificationTrend: 'Verification Trend',
            answerBreakdown: 'Answer Breakdown',
            approved: 'Approved',
            rejectedLabel: 'Rejected',
            partiallyMet: 'Partially Met',
        },
        competency: {
            title: 'Competency Exercise Scores',
            totalTests: 'Total Competency Exercises',
            submitted: 'Submitted',
            inProgress: 'In Progress',
            notStarted: 'Not Started',
            avgScore: 'Average Score',
            testsTaken: 'Competency Exercises Taken',
            latestScore: 'Latest Score',
            rawScore: 'Raw Score',
            completionTrend: 'Completion Trend',
            scoreDistribution: 'Score Distribution',
            scoreByRole: 'Score Distribution by Role',
            userDetail: 'User Details',
            questionBreakdown: 'Question Answer Breakdown',
            totalAnswers: 'Total answers',
            selectedCount: 'Selected',
            pts: 'pts',
        },
        actionPlans: {
            title: '{{actionPlanLabelPlural}}',
            totalPlans: 'Total {{actionPlanLabelPlural}}',
            totalActions: 'Total {{fixedActionLabelPlural}}',
            avgPerPlan: 'Average per Plan',
            aiAcceptance: 'AI Acceptance',
            totalEvidence: 'Total Evidence',
            evidenceRate: 'Evidence Rate',
            plans: '{{actionPlanLabelPlural}}',
            actions: '{{fixedActionLabelPlural}}',
            evidence: 'Evidence',
            plansTrend: '{{actionPlanLabelPlural}} Trend',
            userDetail: 'User Details',
        },
        managers: {
            title: 'Manager Effectiveness',
            adminOnly: 'Admin only — company-wide view',
            totalManagers: 'Total Managers',
            teamSize: 'Team Size',
            teamCompliance: 'Team Compliance',
            pendingValidations: 'Pending Validations',
            avgVerifyTime: 'Average Verify Time',
            detail: 'Manager Details',
            validationsCompleted: 'Validations Done',
            indicatorsReviewed: '{{indicatorLabelPlural}} Reviewed',
            actionPlansFollowedUp: '{{actionPlanLabelPlural}} Followed Up',
            teamComplianceDelta: 'Compliance Delta',
        },
        comingSoon: {
            badge: 'Awaiting Employee Data',
            requiresLabel: 'Requires:',
        },
        sections: {
            reports: 'Reports',
            coreAnalytics: 'Core Analytics',
            executiveCeo: 'Executive Reports',
            learningDev: 'Learning & Development',
            humanResources: 'Human Resources',
            qhsse: 'QHSSE',
            operationsMgmt: 'Operations Management',
            operations: 'Operations',
        },
        supportUsage: {
            title: 'Support Function Usage Index',
            description: 'Shows which support departments actively access, analyse and act on Calibrate data.',
            missingData: 'Department model and per-department activity tracking.',
        },
        trainingDemand: {
            title: 'Training Demand Signal',
            description: 'Aggregated competency gaps by {{creAbbr}} to identify real training needs.',
            totalGaps: 'Total Competency Gaps',
            uniqueExpectations: 'Unique Expectations',
            topCres: 'Top {{creAbbrPlural}} with Gaps',
            gapsByExpectation: 'Gap Count by Expectation',
            expectation: 'Expectation',
            gapCount: 'Gap Count',
            avgCompliance: 'Average Compliance',
            userDetail: 'Expectation Details',
        },
        trainingEffectiveness: {
            title: 'Training Effectiveness Tracker',
            description: 'Before-and-after competency scores following training to prove training impact.',
            missingData: 'Training event records with before/after linkage.',
        },
        wastedTraining: {
            title: 'Wasted Training Index',
            description: 'Training delivered where no competency gap existed to control cost and waste.',
            missingData: 'Training event records.',
        },
        roleFit: {
            title: 'Role Fit & Readiness',
            description: 'Competency versus role expectation alignment.',
            totalUsers: 'Total Users',
            atAboveBaseline: 'At/Above Baseline',
            belowBaseline: 'Below Baseline',
            fitRate: 'Fit Rate',
            avgGap: 'Average Gap',
            fitDistribution: 'Fit Distribution',
            compliance: 'Compliance',
            gap: 'Gap vs Baseline',
            userDetail: 'User Details',
        },
        promotionReadiness: {
            title: 'Promotion & Succession Readiness',
            description: 'Individuals performing above role baseline.',
            totalUsers: 'Total Assessed',
            highPerformers: 'High Performers',
            percentOfWorkforce: '% of Workforce',
            compliance: 'Compliance',
            userDetail: 'High Performer Details',
        },
        underperformance: {
            title: 'Underperformance vs. Support Provided.',
            description: 'Competency gaps alongside {{fixedActionLabelPlural}} offered for fair performance management.',
            underperformingUsers: 'Underperforming Users',
            withActionPlans: 'With {{actionPlanLabelPlural}}',
            withPlansPct: 'With Plans %',
            avgActionsPerUser: 'Average {{fixedActionLabelPlural}}/User',
            compliance: 'Compliance',
            planCount: '{{actionPlanLabelPlural}}',
            actionsCount: '{{fixedActionLabelPlural}}',
            evidenceCount: 'Evidence',
            userDetail: 'User Details',
        },
        safetyCompetency: {
            title: 'Safety-Critical Competency Gaps',
            description: '{{creAbbrPlural}} linked to safety below acceptable thresholds.',
            missingData: 'Safety-critical flag on expectations.',
        },
        auditReadiness: {
            title: 'Audit Readiness Index',
            description: 'Audit-related {{creAbbr}} competency status across teams.',
            missingData: 'Audit-related flag on expectations.',
        },
        incidentCorrelation: {
            title: 'Incident vs. Competency Correlation',
            description: 'Incidents mapped against competency gaps to identify true root causes.',
            missingData: 'Incident tracking model.',
        },
        managerUsage: {
            title: 'Manager Platform Usage Index',
            description: 'Login frequency, time spent, validations, and actions taken per manager.',
            missingData: 'Login and activity history tracking.',
        },
        notificationUsage: {
            title: 'Push Notification Usage & Response',
            description: 'Volume of messages received by staff and employee response rates.',
            totalReceived: 'Total Received',
            totalRead: 'Total Read',
            readRate: 'Read Rate',
            avgPerUser: 'Average per User',
            receivedCount: 'Received',
            readCount: 'Read',
            recipientDetail: 'Recipient Details',
        },
        teamImprovement: {
            title: 'Team Competency Improvement Curve',
            description: 'Rate of competency improvement per team mapped to manager activity.',
            avgImprovementRate: 'Average Improvement',
            bestTeamDelta: 'Best Team Delta',
            worstTeamDelta: 'Worst Team Delta',
            complianceTrend: 'Team Compliance Trend',
            teamSize: 'Team Size',
            startCompliance: 'Start Compliance',
            currentCompliance: 'Current Compliance',
            delta: 'Delta',
            managerDetail: 'Team Details',
        },
        aiUsage: {
            title: 'AI Assistant Usage',
            description: 'AI chatbot message usage and utilisation across the platform.',
            totalUsers: 'Total Users',
            activeUsers: 'Active Users',
            totalMessages: 'Total Messages Used',
            utilizationPct: 'Utilisation %',
            messagesUsed: 'Messages Used',
            messagesLimit: 'Message Limit',
            userDetail: 'Usage by User',
            currentPeriod: 'Current period',
        },
        creHeatmap: {
            title: '{{creAbbr}} Competency Heatmap by Role',
            description: 'Percentage competent, needs support and critical gaps per {{creAbbr}} and role.',
            totalCresAssessed: '{{creAbbrPlural}} Assessed',
            avgCompetency: 'Average Competency',
            heatmap: 'Competency Heatmap',
            expectation: 'Expectation',
            role: 'Role',
            competent: 'Competent %',
            needsSupport: 'Needs Support %',
            critical: 'Critical %',
            legend: 'Legend',
            roleDetail: 'By Role Details',
        },
        safetyExposure: {
            title: 'Safety-Critical {{creAbbr}} Exposure',
            description: 'Number of employees below standard in safety-linked {{creAbbrPlural}}.',
            missingData: 'Safety-critical flag on expectations.',
        },
        actionClosure: {
            title: '{{actionPlanLabel}} Closure Rate',
            description: 'Open versus closed {{fixedActionLabelPlural}} by team.',
            totalActions: 'Total {{fixedActionLabelPlural}}',
            openActions: 'Open {{fixedActionLabelPlural}}',
            closedActions: 'Closed {{fixedActionLabelPlural}}',
            closureRate: 'Closure Rate',
            closureDistribution: 'Open vs Closed',
            open: 'Open',
            closed: 'Closed',
            teamDetail: 'Team Details',
        },
        utilisation: {
            title: 'Platform Utilisation',
            description: 'How actively your team is using the platform.',
            totalUsers: 'Total Users',
            loggedInEver: 'Logged In (Ever)',
            activeInPeriod: 'Active in Period',
            cresReviewed: '{{creAbbr}} Reviewed',
            indicatorSubmitted: '{{indicatorLabel}} Submitted',
            managersVerified: 'Managers Verified',
            withActionPlan: '{{actionPlanLabel}} Created',
            chatbotActive: 'Calibrate AI Assistant Active',
            aiTouAgreed: 'AI Agreement Signed',
            pendingInvites: 'Pending Invites',
            pendingInvitesHelp: 'Users invited but who have never logged in.',
            userDetail: 'User Detail',
            lastSeen: 'Last Seen',
            never: 'Never',
        },
        teamGlance: {
            title: 'Your Team At A Glance',
            description: 'A quick overview of where your team stands.',
            noReports: 'This page shows the people in your reporting line. Nobody reports to you yet.',
            subtitle_one: '{{name}}\'s team · {{count}} person · Data as of {{date}}',
            subtitle_other: '{{name}}\'s team · {{count}} people · Data as of {{date}}',
            subtitleArea_one: '{{name}}\'s area · {{teams}} teams · {{count}} person · Data as of {{date}}',
            subtitleArea_other: '{{name}}\'s area · {{teams}} teams · {{count}} people · Data as of {{date}}',
            subtitleAll_one: 'All people · {{count}} person · Data as of {{date}}',
            subtitleAll_other: 'All people · {{count}} people · Data as of {{date}}',
            standardChanged_one: 'The self-assessment standard changed for {{count}} person since their last assessment, so Decreased and Improved are not shown for them.',
            standardChanged_other: 'The self-assessment standard changed for {{count}} people since their last assessment, so Decreased and Improved are not shown for them.',
            viewerRole: {
                admin: 'Admin',
                manager: 'Manager',
            },
            byTeam: {
                title: 'By team',
                reportingManager: 'Reporting Managers',
                people: 'People',
                needsAttention: 'Needs attention',
                positiveIndicators: 'Positive indicators',
                overdue: 'Overdue',
                checkedActions: 'Actions checked',
                aiUsed: 'Assistant usage',
            },
            people: {
                name: 'Name',
                role: 'Role',
                manager: 'Manager',
                why: 'Why',
                count_one: '{{count}} person',
                count_other: '{{count}} people',
                showing: '{{count}} people, showing {{shown}}',
                showAll: 'Show all {{count}}',
                showFewer: 'Show fewer',
            },
            needsAttention: {
                title: 'Needs attention',
                subtitle: 'People and issues requiring management attention',
                note: 'People and issues requiring management attention',
                empty: 'Nobody needs attention right now. Everyone is up to date and no score has decreased since their last self-assessment.',
            },
            positiveIndicators: {
                title: 'Positive indicators',
                empty: 'Nobody has positive indicators right now.',
            },
            across: {
                title: 'Snapshot overview of team',
                cannotDo: 'Reported Competency Gaps',
                cannotDoEmpty: 'Nobody has marked an expectation "cannot do".',
                incorrect: 'Most below role expectation answers',
                incorrectEmpty: 'No competency exercise answers yet.',
                outstanding: 'Overdue & Outstanding',
                assessmentsOverdue: 'Self-assessment overdue',
                exercisesOverdue: 'Competency exercise overdue',
                awaitingVerification: 'Awaiting your verification over 7 days',
                noAiUse: 'Have not used the AI assistant this month',
            },
            inWords: {
                title: 'In words',
                generatedAt: 'Generated at {{time}} · AI-written from the figures above',
                generatedAtShort: '{{time}} · AI-written',
                regenerate: 'Regenerate',
                unavailable: 'The written summary is not available right now. The lists above are complete and up to date. Try again in a few minutes.',
                regenerationsUsed: '{{used}} of {{limit}} regenerations used today',
                tryAgain: 'Try again',
                generating: 'Writing the summary from the figures above…',
                regenerating: 'Regenerating…',
            },
            help: {
                columnTitle: '{{section}} - {{column}}',
                columns: {
                    reportingManager: 'Managers who report directly to you',
                    people: 'Number of people in each team',
                    teamNeedsAttention: 'Competency concerns and other issues requiring attention',
                    teamPositive: 'Improvements, strong exercise results and completed action plans',
                    teamOverdue: 'Self-assessments and competency exercises overdue',
                    checkedActions: 'Action-plan outputs worked on by employees and available for verification',
                    aiUsed: 'Number of people using AI assistant to support role performance',
                    name: 'The person. Select the name to open their profile.',
                    role: 'The person\'s current role.',
                    manager: 'The person\'s direct manager. Shown when you see more than one team.',
                    whyAttention: 'Every reason that applies to this person, with the detail in brackets.',
                    whyPositive: 'Every positive indicator for this person, with the detail in brackets.',
                },
            },
            pills: {
                needsAttention: 'Needs attention',
                positiveIndicators: 'Positive indicators',
                overdue: 'Overdue',
                awaitingVerification: 'Awaiting your verification',
                aiAssistantUsed: 'Used the AI Assistant Usage this month',
                ofTotal: '{{count}} of {{total}}',
            },
        },
        actionPlanReviews: {
            title: '{{actionPlanLabel}} Outcomes',
            summaryNavLabel: '{{actionPlanLabel}} Outcomes Summary',
            summaryTitle: '{{actionPlanLabel}} Outcomes Summary',
            summaryIntro: 'An aggregated summary with no individual staff information, suitable for sharing (e.g. with Governors).',
            openSummary: 'Summary for sharing',
            print: 'Print / Save as PDF',
            periodNote: 'Based on reviews completed in the selected period',
            periodRange: '{{start}} to {{end}}',
            prepared: 'Prepared {{date}}',
            reviewed: 'Reviewed',
            ofStaffWithPlan: 'of {{count}} staff with an {{actionPlanLabel}}',
            tabSummary: 'Summary',
            tabStaff: 'Staff',
            tabActions: 'Actions',
            overallReviews: 'Overall {{actionPlanLabel}} reviews',
            actionReviews: 'Individual action reviews',
            byRole: 'By role',
            byManager: 'By manager',
            manager: 'Manager',
            status: 'Status',
            overall: 'Overall',
            reviewedOn: 'Reviewed on',
            action: 'Action',
            outcome: 'Outcome',
            comment: 'Comment',
            total: 'Total',
            staffReviewed: 'Staff reviewed',
            combinedGroups: 'Other groups combined ({{count}})',
            tooFewToShow: 'Fewer than {{min}} staff in total — not shown',
            noReviews: 'No reviews have been completed in this period.',
            noReviewsEarlier: 'No reviews have been completed in this period. The most recent review was completed on {{date}}, before the period started.',
            keyFindings: 'Key findings',
            headlineFigures: 'Headline figures',
            narrativeCoverage: 'Of {{withPlan}} staff with an {{actionPlanLabel}}, {{reviewed}} were reviewed in this period ({{awaiting}} awaiting review).',
            narrativeOverall: 'Overall, {{met}}% of {{actionPlanLabelPlural}} were reviewed as Met and {{notMet}}% as Not Met.',
            narrativeActions: 'Across {{total}} individual actions, {{met}}% were Met and {{notMet}}% Not Met.',
            privacyNote: 'To protect individual privacy, groups with fewer than {{min}} reviewed staff are combined, and no individual staff information is included in this summary.',
            methodNote: 'Where a member of staff had more than one {{actionPlanLabel}} reviewed in the period, the most recent review is counted.',
        },
    },
};

export default en;
